import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Swords, Flame, Hourglass, Check, Upload, MessageSquare, Pin,
  ShieldCheck, Trophy, AlertTriangle, ChevronLeft,
  Flag, Pencil, X, Save, Cpu, Bell, Ban,
} from 'lucide-react';
import {
  api,
  parseApiError,
  type MatchCardV2Response,
  type MatchCardStatus,
  type SubmissionSlotRow,
  type SlotStatus,
  type MapVetoState,
  type Me,
  type ScoreCellField,
  type ShotQualityFlag,
  type Competition,
  type OcrFeedEvent,
  type ServerCellConsensusRow,
  type MatchCardGameResult,
  type MatchSummary,
} from '../lib/api';
import { cn } from '../lib/cn';
import { clanLabelFrom } from '../lib/displayName';
import { clanLabel, MODE_LABEL } from '../lib/matchCardLabels';
import { aggregateConsensus, mergeServerConsensus, applyUploaderStripPrecedence, type ConsensusCell as CellAggregate } from '../lib/consensus';
import { useEffectiveRole, useEffectiveMe, useViewAs } from '../lib/viewAs';
import { hasOrgManagerMembershipOfEntity } from '../lib/orgAccess';
import { canPokeSide, canProxyUploadSide } from '../lib/matchCaptainAccess';
import { submissionWindowOpen } from '../lib/submissionWindow';
import { ScoreFlagButton } from '../components/ScoreFlagButton';
import { ScoreFlagInbox } from '../components/ScoreFlagInbox';
import { OcrActivityFeedPanel } from '../components/OcrActivityFeedPanel';
import { MatchRosterInitPanel } from '../components/MatchRosterInitPanel';
import { CardAgreementPanel } from '../components/CardAgreementPanel';
import { MatchDateAgreementControl } from '../components/MatchDateAgreementControl';
import { ImageDropzone } from '../components/ImageDropzone';
import { ShotInspectGrid } from './ShotInspectGrid';
import { SetupProgressStrip } from '../components/SetupProgressStrip';
import { useMatchVerifyChecklist } from '../lib/matchVerifyChecklist';
import { useSectionHighlightController } from '../lib/sectionHighlight';
import { SignoffPanel } from '../components/SignoffPanel';
import { isMatchSideCaptain } from '../lib/matchCaptainAccess';
import { fireEdgeSparkFlourish } from '../lib/sectionSparkFlourish';
import { VetoBoard } from '../components/VetoBoard';

// ─────────────────────────────────────────────────────────────────────────────
// MatchCardLive — Match Card v2 UI.
//
// Living calendar object: scheduled → veto → live submission → forged.
// Read-heavy page driven by polling. Per-slot upload is the only write surface
// for ordinary participants. Admins get transition + forge controls.
// ─────────────────────────────────────────────────────────────────────────────

function stripClanPrefix(s: string | undefined): string { return (s || '').replace(/^CLAN#/, ''); }

function formatDateTime(ts?: number): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeUntil(ts: number): string {
  const ms = ts - Date.now();
  if (ms <= 0) return 'now';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `in ${h}h`;
  return `in ${Math.floor(h / 24)}d`;
}

// ─────────────────────────────────────────────────────────────────────────────

// Exported for MatchCardLive.test.ts (map-completeness + fallback tests).
export const STATUS_THEMES: Record<MatchCardStatus, { bg: string; border: string; text: string; pulse: boolean; label: string }> = {
  DRAFT:     { bg: 'bg-iron-800', border: 'border-iron-600', text: 'text-iron-300', pulse: false, label: 'DRAFT' },
  SCHEDULED: { bg: 'bg-iron-800', border: 'border-iron-600', text: 'text-iron-200', pulse: false, label: 'SCHEDULED' },
  VETO:      { bg: 'bg-amber-900/30', border: 'border-amber-600/60', text: 'text-amber-300', pulse: false, label: 'VETO OPEN' },
  LIVE:      { bg: 'bg-forge-900/40', border: 'border-forge-500/70', text: 'text-forge-300', pulse: true,  label: 'LIVE NOW' },
  CLOSED:    { bg: 'bg-iron-800', border: 'border-iron-600', text: 'text-iron-200', pulse: false, label: 'CLOSED — AWAITING OCR' },
  OCR:       { bg: 'bg-blue-900/30', border: 'border-blue-600/60', text: 'text-blue-300', pulse: true,  label: 'OCR PROCESSING' },
  VERIFIED:  { bg: 'bg-emerald-900/30', border: 'border-emerald-600/60', text: 'text-emerald-300', pulse: false, label: 'VERIFIED' },
  FORGED:    { bg: 'bg-iron-700', border: 'border-iron-500', text: 'text-forge-300', pulse: false, label: 'FORGED' },
  CANCELLED: { bg: 'bg-iron-800', border: 'border-red-700/40', text: 'text-red-400', pulse: false, label: 'CANCELLED' },
  DISPUTED:  { bg: 'bg-red-900/30', border: 'border-red-600/60', text: 'text-red-300', pulse: false, label: 'DISPUTED' },
  // W-A defect 3: previously an unstyled magic string — the runner wrote
  // cardStatus: 'FAILED' with no theme, no enum member, no admin path out.
  FAILED:    { bg: 'bg-red-950/50', border: 'border-red-700/70', text: 'text-red-400', pulse: false, label: 'FAILED — NEEDS RETRY' },
  // Same defect class as FAILED above: the runner writes cardStatus: 'MANUAL'
  // on the depleted-org-tokens fork (orgTokenFallback), which was missing
  // from the frontend union — the themeless lookup crashed the whole card.
  MANUAL:    { bg: 'bg-iron-800', border: 'border-amber-600/40', text: 'text-amber-200', pulse: false, label: 'MANUAL SCORING' },
};

// Unknown/future statuses degrade to a visible neutral banner instead of
// crashing the card — the raw status string becomes the label so the gap is
// loud on screen, not silent.
export function themeFor(status: MatchCardStatus): (typeof STATUS_THEMES)[MatchCardStatus] {
  const theme = STATUS_THEMES[status];
  if (theme) return theme;
  console.warn(`[MatchCardLive] no theme for cardStatus "${status}" — falling back`);
  return { bg: 'bg-iron-800', border: 'border-iron-600', text: 'text-iron-300', pulse: false, label: String(status) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Confirm-before-forge gate (Lisa, 2026-07-14 — the "beautiful final card
// stuck on PENDING" trap): cardStatus (this page's OCR/publish lifecycle,
// STATUS_THEMES above) and the match's RESULT status (PENDING → CONFIRMED,
// written ONLY by "Confirm scores" / handleConfirmScores — the thing that
// derives games[]/nightWinner and is what makes a match reach STANDINGS) are
// two independent fields. cardStatus could reach FORGED (terminal) while
// the result stayed PENDING forever — a final-looking public card that
// never appears in standings, with the Confirm-scores panel above gone
// (it's hidden once FORGED). This is UX only, preventing the dead-end click
// — the real gate is matchCardV2.ts's isResultConfirmed check on the
// /transition and /forge routes; both must agree independently.
// Exported for MatchCardLive.test.ts.
export function isForgeBlocked(match: { status?: string } | undefined | null): boolean {
  return match?.status !== 'CONFIRMED';
}

export const FORGE_BLOCKED_HINT =
  "Confirm scores first — forging locks the card, and a match must be committed to standings before it's finalised.";

// ─────────────────────────────────────────────────────────────────────────────

export function MatchCardLive() {
  const { matchId: rawMatchId } = useParams();
  const [qs] = useSearchParams();
  const compId = qs.get('compId') || '';
  const matchId = rawMatchId ? decodeURIComponent(rawMatchId) : '';

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  // Needed only to resolve this comp's parent orgId for the OCR-digestion
  // org-tier gate (canOperateDigestion below) — see MyCompDetail.tsx for the
  // established pattern this mirrors.
  const { data: allComps } = useQuery({ queryKey: ['competitions'], queryFn: api.competitions });

  const cardQuery = useQuery({
    queryKey: ['matchcard', matchId, compId],
    queryFn: () => api.matchCardGet(matchId, compId),
    enabled: !!matchId && !!compId,
    refetchInterval: 4000,
  });

  useEffect(() => {
    if (cardQuery.data) {
      const m = cardQuery.data.match;
      document.title = `${clanLabelFrom(m.clan1DisplayName, m.clan1)} vs ${clanLabelFrom(m.clan2DisplayName, m.clan2)} — GG Anvil`;
    }
  }, [cardQuery.data]);

  if (!matchId || !compId) {
    return <Shell><EmptyState text="Missing match or competition id." /></Shell>;
  }
  if (cardQuery.isLoading) {
    return <Shell><EmptyState text="Loading match card…" /></Shell>;
  }
  if (cardQuery.isError || !cardQuery.data) {
    return <Shell><EmptyState text="Match not found." kind="error" /></Shell>;
  }

  const comp = (allComps ?? []).find(c => c.id === compId);

  return (
    <Shell>
      <CardBody data={cardQuery.data} me={me} matchId={matchId} compId={compId} comp={comp} focus={qs.get('focus')} />
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/calendar" className="inline-flex items-center gap-1 text-sm text-iron-400 hover:text-iron-200">
          <ChevronLeft size={16} /> Calendar
        </Link>
        <span className="text-iron-700">·</span>
        <Link to="/matches" className="text-sm text-iron-400 hover:text-iron-200">All matches</Link>
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text, kind = 'info' }: { text: string; kind?: 'info' | 'error' }) {
  return (
    <div className="rounded-xl border border-iron-700 bg-iron-900 px-6 py-16 text-center">
      <p className={cn('font-mono text-sm', kind === 'error' ? 'text-red-400' : 'text-iron-400')}>{text}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CardBody({ data, me, matchId, compId, comp, focus }: {
  data: MatchCardV2Response; me?: Me; matchId: string; compId: string; comp?: Competition;
  // Deep-link target — `?focus=veto` (from the Discord "Open the veto" DM
  // button) scrolls to and pulses the veto section on arrival. Null otherwise.
  focus?: string | null;
}) {
  const queryClient = useQueryClient();
  const { match, slotsByGame, comments, summary } = data;
  const status: MatchCardStatus = (match.cardStatus ?? 'DRAFT') as MatchCardStatus;
  const theme = themeFor(status);

  const clan1 = match.clan1DisplayName ?? stripClanPrefix(match.clan1);
  const clan2 = match.clan2DisplayName ?? stripClanPrefix(match.clan2);
  // Pure-display label — formats the fallback (no displayName stamp) through
  // displayClanName so underscores/EXACT_BRANDS render properly (Lisa,
  // 2026-07-14). clan1/clan2 ABOVE stay untouched and keep feeding the two
  // data paths that need the raw/unformatted form: MatchRosterInitPanel's
  // clan{N}Name (its clan1Key fallback = clan1ClanId ?? clan1Name is used as
  // an actual api.clan() lookup key) and OcrDigestionPanel's spawned.clan{N}
  // (ShotInspectGrid's isSameClan string-compares it against slot.clanId —
  // a formatted label would break that match, same defect class as its own
  // 2026-07-13 doc comment describes). Only use clan1Label/clan2Label below
  // for sites that render clan name as human-visible text.
  const clan1Label = clanLabelFrom(match.clan1DisplayName, match.clan1);
  const clan2Label = clanLabelFrom(match.clan2DisplayName, match.clan2);
  // Match-team-autofill (Lisa, 2026-07-13): the REAL clan pk each side
  // resolves to — mirrors the backend's resolveMatchClanPk (index.ts):
  // prefer the stamped clan{N}Id (a team-entry side, whose clan1/clan2 may
  // be a qualified "Beta (Alpha)" name GET /clan/:name can't resolve), else
  // fall back to clan1/clan2 itself (a legacy side, where the bare name IS
  // already the real clan identity). Used ONLY for API calls that need a
  // real clan (MatchRosterInitPanel's seat fetch) — clan1/clan2Name above
  // stay the DISPLAY labels.
  const clan1ClanId = match.clan1Id ?? match.clan1;
  const clan2ClanId = match.clan2Id ?? match.clan2;
  const format = match.format ?? 'LEGACY';
  const gameModes = match.gameModes ?? [];
  // Use the *effective* role (view-as-aware) so founders can simulate
  // member/leader and lose edit privileges as expected. Real-role check
  // would let founders edit even when posing as a member, defeating the
  // purpose of the view-as toggle.
  const effectiveRole = useEffectiveRole(me?.role);
  // View-as-aware copy of `me`. Originally added for the org-membership leg
  // below (canManageMatchOrg) — an "org admin (scoped)" simulation carries a
  // real ORG_ADMIN/ORG_OWNER membership row through this, same as
  // effectiveRole does for the platform-role leg.
  //
  // Widened 2026-08-03 to carry the IDENTITY too. Every "is this row mine?"
  // check on this page — which side I lead, whose id signs the agreement,
  // which slot is mine — compares against a discordId, and they were all
  // still reading the RAW one while the role and org gates beside them were
  // view-as-aware. lib/viewAs.ts is explicit that the id IS a grant, and the
  // most powerful one on the platform (see VIEW_AS_SIM_DISCORD_ID): a raw
  // comparison silently reinstated it mid-preview, so a founder previewing a
  // member could still sign a real match agreement, act on the real veto and
  // upload against their own real slots — writes the backend attributes to
  // the real founder, because the toggle is client-side and the token is
  // unchanged. Every `me`-derived identity and every `me` handed to a child
  // now flows from HERE. Identity no-op whenever no view-as is active.
  const effectiveMe = useEffectiveMe(me);
  const { viewAs } = useViewAs();
  // Children typed `me?: Me` — useEffectiveMe's null (a null `me` in, a null
  // `me` out) normalises to undefined so the prop keeps its exact contract.
  const effectiveMeProp = effectiveMe ?? undefined;
  const isAdmin = effectiveRole === 'founder' || effectiveRole === 'admin';
  // Per-clan leadership: proxy-upload is scoped to the leader's OWN clan
  // (backend already 403s cross-clan uploads — C1 sanity check in
  // matchCardV2.ts — so the UI must not offer buttons the server refuses).
  // Admin/founder act for both sides via isAdmin.
  const isLeader1 = effectiveMe?.discordId === match.leader1Id;
  const isLeader2 = effectiveMe?.discordId === match.leader2Id;
  const isLeader = isLeader1 || isLeader2;

  // Match date scheduling (division fixtures & weekly schedule, moved onto
  // the match card 2026-07-18 — Lisa's info-architecture split: "Divisions
  // is for RESULTS, Matches is to CREATE and VIEW matches (incl.
  // scheduling)"). groupIndex is stamped ONLY by generateDivisionsPlay on a
  // DIVISIONS-stage fixture (see MatchSummary.groupIndex's doc comment,
  // lib/api.ts) — ROUND_ROBIN/ELIMINATION_*/SWISS matches never carry it, so
  // this is the same discriminator DivisionsScoreboardPanel's old fixtures
  // table used to decide "is this a division match" before that table moved
  // here. `match` here is MatchCardV2Response['match'] (Record<string,
  // unknown> & {...}), not MatchSummary — groupIndex rides the index
  // signature, typed `unknown` until this narrows it.
  const isDivisionMatch = typeof match.groupIndex === 'number';
  // MatchDateAgreementControl was built against MatchSummary (its original
  // home was a GET /matches-shaped row) — this card's `match` is a
  // differently-shaped raw row (cardStatus/forgedCardUrl/etc alongside the
  // same underlying MATCH# fields), so adapt just the fields the control
  // actually reads rather than reshaping the whole card. `status` defaults
  // to 'PENDING' only to satisfy MatchSummary's required field; the control
  // never reads that fallback as meaningful (it only checks
  // dateAgreementStatus/scheduledAt), and `showDateControl` below never
  // renders it once match.status really is CONFIRMED.
  const dateControlMatch: MatchSummary = {
    pk: match.pk,
    sk: match.sk,
    clan1: match.clan1,
    clan2: match.clan2,
    clan1DisplayName: match.clan1DisplayName,
    clan2DisplayName: match.clan2DisplayName,
    status: match.status ?? 'PENDING',
    leader1Id: match.leader1Id,
    leader2Id: match.leader2Id,
    scheduledAt: match.scheduledAt,
    proposedScheduledAt: match.proposedScheduledAt,
    dateProposedBy: match.dateProposedBy,
    dateAgreementStatus: match.dateAgreementStatus,
  };
  // Window: past DRAFT (the card's been built — MatchRosterInitPanel above
  // already lets a leader/admin set the INITIAL date while building it, this
  // control is for CHANGING it afterward) and short of CONFIRMED (once the
  // result is in, the date is history, not something left to schedule).
  const showDateControl = isDivisionMatch && status !== 'DRAFT' && match.status !== 'CONFIRMED';

  // OCR digestion trigger/feed authz — mirrors the backend's
  // canOperateOcrDigestion (matchCardV2.ts): founder, or ORG_OWNER/ORG_ADMIN
  // of THIS comp's parent org. This is a deliberately DIFFERENT boundary
  // from `isAdmin` above (Discord-native founder/admin) — a Discord
  // "admin" who isn't an org manager of this specific comp's org does not
  // get the digestion controls, and vice versa. The founder leg goes
  // through effectiveRole (view-as-aware, so founders previewing as member/
  // leader correctly lose this too); the org-membership leg now reads
  // effectiveMe too (2026-07-16 View-As audit — org roles ARE part of the
  // view-as simulation model now, via the "org admin (scoped)" mode): an
  // org-admin-of-THIS-comp's-org simulation correctly grants it, and any
  // other simulation correctly does not (effectiveMe's orgMemberships is
  // empty for a plain platform-role sim — see lib/viewAs.ts's useEffectiveMe).
  const orgMembership = comp?.orgId ? (effectiveMe?.orgMemberships ?? []).find(m => m.orgId === comp.orgId) : undefined;
  const canOperateDigestion = effectiveRole === 'founder'
    || orgMembership?.role === 'ORG_OWNER'
    || orgMembership?.role === 'ORG_ADMIN';

  // Score-inspect/confirm/edit widen (org-admin de-gate, auth/RBAC work): an
  // org manager (ORG_ADMIN/ORG_OWNER) of THIS comp's parent org gets the same
  // access isAdmin already has for the score-screenshot inspect drawer
  // (canInspect below), the consensus cleanup editor (ScoreboardConsensus's
  // canEdit), and the confirm-scores control (ConfirmScoresPanel) — mirrors
  // the backend's EXACT trio (handleConfirmScores / the widened
  // handleSlotImageUrl: ctx.isAdmin || isOrgCompManager || isCompStructureManager).
  // Uses the SINGLE reusable orgAccess helper — same split canOperateDigestion
  // above uses: the founder/admin leg is `isAdmin` (view-as-aware);
  // the org-membership leg reads effectiveMe (view-as-aware — an org-admin-
  // scoped simulation of this comp's org is seen here too).
  const canManageMatchOrg = hasOrgManagerMembershipOfEntity(effectiveMe, comp);
  const canManageScores = isAdmin || canManageMatchOrg;

  // Who may sign off EACH side's own rows (stage 2, 2026-07-22). Mirrors the
  // backend's canSignOffSide (shared/matchSignoff.ts): that side's clan leader,
  // the captain of the team THAT SIDE FIELDED, or an operator on an
  // unresponsive side's behalf. Captain scoping goes through
  // isMatchSideCaptain, which already mirrors isActiveSideCaptain's exact
  // teamNo match — never a second derivation of "is this my team".
  // UX gating only; the server is the real gate.
  // The CAPTAIN leg of "me" — the third grant this page reads, after the role
  // and the discordId. `viewerCaptainOf` is the REAL caller's captaincies,
  // computed server-side, and the server does not role-filter it under a
  // preview — so it still describes the founder, not the persona. Dropped
  // while previewing, exactly as PublicMatchCard.tsx already does for this
  // same field. Without this, a founder who genuinely captains some real team
  // kept, through every preview: agree-for-this-side (runsSideN below),
  // result sign-off (canSignN), and — via the SlotGrid hand-off further down,
  // which must read THIS sanitised value and never `data.viewerCaptainOf`
  // again — poke and proxy-upload. All real writes against a live match.
  // FAIL-CLOSED deliberately, same reasoning as PublicMatchCard's. Identity
  // no-op whenever no view-as is active, so a real captain loses nothing.
  const viewerCaptainOf = viewAs ? undefined : data.viewerCaptainOf;
  const isCaptain1 = isMatchSideCaptain(viewerCaptainOf, match.clan1, match.clan1TeamNo);
  const isCaptain2 = isMatchSideCaptain(viewerCaptainOf, match.clan2, match.clan2TeamNo);
  const canSign1 = canManageScores || isLeader1 || isCaptain1;
  const canSign2 = canManageScores || isLeader2 || isCaptain2;

  // Captain widen (Lisa, 2026-07-22): "a captain needs to captain their own
  // team". A captain now runs the same night-of controls a leader does —
  // building the card, map veto, reporting the match finished, and reviewing
  // their OWN side's screenshots. All four are enforced server-side and
  // team-scoped there (resolveMatchCaptaincy -> isActiveSideCaptain, which
  // matches the grant to the team THAT SIDE fielded); these are UX gates.
  const isCaptainOfEitherSide = isCaptain1 || isCaptain2;
  const isSideRunner = isLeader || isCaptainOfEitherSide;
  // The participant status bar is for people who are NOT operators — an
  // operator already has the full bar.
  const showParticipantBar = isSideRunner && !canManageScores;
  // Own-side screenshot review: restored access, never the opponent's.
  const canInspect1 = canManageScores || isLeader1 || isCaptain1;
  const canInspect2 = canManageScores || isLeader2 || isCaptain2;

  // Verification checklist (Lisa, 2026-07-22) — the same device the org/comp
  // go-live strips use, pointed at "where am I in verifying this match". Only
  // mounted for people who can actually act on it: a list of chores shown to
  // someone with no controls is noise. Reads the SAME query keys the panels
  // below already read, so it can never drift from them.
  const verifyChecklist = useMatchVerifyChecklist(matchId, compId, canManageScores);

  // Same wiring TournamentSetup uses (:168-182): the spark flourish is
  // additive on top of sectionHighlight's own scroll/pulse/focus, fired from
  // its onHighlight extension point rather than re-deriving the timing.
  // Stable across renders so highlightNow's identity doesn't churn.
  const onSectionHighlight = useCallback((_id: string, el: HTMLElement) => {
    fireEdgeSparkFlourish(el);
  }, []);
  const highlight = useSectionHighlightController(onSectionHighlight);

  // Deep-link from the "Open the veto" DM button (?focus=veto): scroll to and
  // pulse the veto section once on arrival. highlightNow already retries until
  // the section mounts, so this fires safely even before the card finishes
  // rendering. Guarded to run once — re-running on every poll refetch would
  // yank the page back to the veto mid-interaction.
  const didFocus = useRef(false);
  useEffect(() => {
    if (didFocus.current || focus !== 'veto') return;
    didFocus.current = true;
    highlight.highlightNow('match-veto');
  }, [focus, highlight]);

  return (
    <div className="space-y-4">
      {canManageScores && verifyChecklist && (
        <SetupProgressStrip
          mode="comp"
          title="Verification"
          items={verifyChecklist.items}
          warning={verifyChecklist.warning}
          // The SAME "over here" mechanism the org/comp strips use — scroll to
          // a consistent focal position, pulse the section, auto-focus its
          // first empty control, and fire the edge-spark flourish. A plain
          // scrollIntoView (what this was first built with) lands you next to
          // the right control without ever saying WHICH control — exactly the
          // complaint that produced sectionHighlight.tsx in the first place.
          onJump={item => item.sectionId && highlight.highlightNow(item.sectionId)}
        />
      )}
      {/* ── Match agreement gate (2026-07-17) — a captain-proposed match
          lands here (match.status, NOT cardStatus above) until the opponent
          captain responds. Deliberately rendered ABOVE the cardStatus banner
          below — the agreement gate blocks the whole match from going live,
          so it takes priority over whatever the OCR/publish card lifecycle
          is currently showing. */}
      {match.status === 'AWAITING_AGREEMENT' && (
        <AgreementBanner
          matchId={matchId}
          compId={compId}
          match={match}
          isLeader1={isLeader1}
          isLeader2={isLeader2}
          clan1Label={clan1Label}
          clan2Label={clan2Label}
        />
      )}

      {/* ── Status banner ───────────────────────────────────────────────── */}
      <div className={cn(
        'rounded-xl border-2 px-5 py-4 transition-shadow',
        theme.bg, theme.border, theme.pulse && 'shadow-[0_0_40px_-8px_rgba(var(--accent-rgb),0.55)]',
      )}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className={cn('flex items-center gap-3 font-display text-lg font-extrabold uppercase tracking-wider', theme.text)}>
            {theme.pulse && <Flame size={20} className="animate-pulse" />}
            {!theme.pulse && <Hourglass size={18} />}
            <span>{theme.label}</span>
          </div>
          <div className="flex items-center gap-3">
            {match.streamerUrl && (
              <a
                href={match.streamerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-red-500/60 bg-red-950/40 px-3 py-1 font-display text-xs font-bold uppercase tracking-wider text-red-200 transition-colors hover:border-red-400 hover:bg-red-900/50"
              >
                <Flame size={12} /> Watch live
              </a>
            )}
            <div className="font-mono text-xs text-iron-400">
              {match.scheduledAt ? `${formatDateTime(match.scheduledAt)} (${timeUntil(match.scheduledAt)})` : 'No scheduled time'}
            </div>
          </div>
        </div>
      </div>

      {/* ── Match date scheduling (division matches only) — propose/agree/
          decline a date & time, self-gated to the two clan leaders inside
          MatchDateAgreementControl itself. No weekRange here (unlike its
          original Divisions-page home) — there's no week window on a match
          card, so the control just defaults its draft to now and never shows
          the "outside this week" nudge. */}
      {showDateControl && (
        <MatchDateAgreementControl
          compId={compId}
          match={dateControlMatch}
          me={effectiveMeProp}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] })}
        />
      )}

      {/* ── Match heading ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-4 px-1 font-mono text-xs text-iron-500">
        <span className="inline-flex items-center gap-1"><Swords size={12} /> Match {String(matchId).replace(/^MATCH#/, '')}</span>
        <span>·</span>
        <span className="font-display font-bold uppercase tracking-wider text-iron-300">{format === 'LEGACY' ? 'LEGACY' : `BEST OF ${format.replace('BO', '')}`}</span>
        {match.teamSize && (<><span>·</span><span>{match.teamSize}v{match.teamSize}</span></>)}
      </div>

      {/* ── Clan plates ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr]">
        <ClanPlate clanId={stripClanPrefix(match.clan1)} label={clan1Label} roster={match.clan1RosterSnapshot} names={match.clan1Display} side="left" />
        <div className="flex items-center justify-center font-display text-2xl font-extrabold tracking-widest text-iron-500">VS</div>
        <ClanPlate clanId={stripClanPrefix(match.clan2)} label={clan2Label} roster={match.clan2RosterSnapshot} names={match.clan2Display} side="right" />
      </div>

      {/* ── Build the match card (G3b-W0) ───────────────────────────────────
          DRAFT has no format/rosters/slots yet — POST /matchcard/:matchId/init
          (matchCardInit) is what plans them, and it needs real playerIds
          picked off each clan's actual seats (ghost seats included, by their
          canonical id — see MatchRosterInitPanel's doc comment). Same authz
          window as handleInit itself: admin, or either clan's leader. A
          DRAFT card viewed by anyone else just shows a quiet "not set up"
          note — nothing to interact with until a leader/admin builds it. */}
      {/* ── Date & time agreement (Lisa, 2026-07-23) ──────────────────────────
          Rendered ABOVE the build panel because it is what now BUILDS the card
          on the normal path — the picker below is the exception route (a clan
          with more seats than the competition allows, where somebody must
          genuinely choose). Shown to EVERYONE who can see the card, not just
          signatories: "who are we waiting on" is the question every viewer of
          an unbuilt card has, and it was answerable nowhere until now. */}
      {status === 'DRAFT' && (
        <CardAgreementPanel
          matchId={matchId}
          compId={compId}
          match={match}
          clan1Label={clan1Label}
          clan2Label={clan2Label}
          scheduledAt={typeof match.scheduledAt === 'number' ? match.scheduledAt : undefined}
          viewer={{
            discordId: effectiveMe?.discordId,
            // Same identity the server derives the party from — leader of a
            // side, or the captain of the team THAT SIDE fielded.
            runsSide1: isLeader1 || isCaptain1,
            runsSide2: isLeader2 || isCaptain2,
            isOrganiser: canManageScores,
          }}
          // T1b (2026-07-24) — same clan-identity + ceiling inputs
          // MatchRosterInitPanel gets below, so the agreement-time squad
          // picker resolves the SAME seats/team-scope the manual path does.
          clan1ClanId={clan1ClanId}
          clan2ClanId={clan2ClanId}
          clan1TeamNo={match.clan1TeamNo}
          clan2TeamNo={match.clan2TeamNo}
          compTeamSize={comp?.teamSize}
        />
      )}

      {/* ── The manual picker is the EXCEPTION route (Lisa, 2026-07-23) ──────
          It builds the card WITHOUT the agreement, and because it was the
          loudest control on a DRAFT card it became the way cards got built —
          which is precisely why the agreement never ran on the first live run.
          It is now folded away by default and unfolds ONLY when the server has
          said a human is genuinely needed (cardBuildNeedsPicker, stamped by
          matchLifecycle when a side's seats cannot be resolved to a squad
          without guessing). Still reachable in every other case, deliberately
          quietly: never remove the escape hatch, just stop advertising it as
          the front door. */}
      {status === 'DRAFT' && (isAdmin || isSideRunner) && (
        <ManualPickerDisclosure needed={match.cardBuildNeedsPicker === true} why={typeof match.cardBuildNeedsPickerWhy === 'string' ? match.cardBuildNeedsPickerWhy : undefined}>
          <MatchRosterInitPanel
            matchId={matchId}
            compId={compId}
            clan1Name={clan1}
            clan2Name={clan2}
            clan1ClanId={clan1ClanId}
            clan2ClanId={clan2ClanId}
            clan1TeamNo={match.clan1TeamNo}
            clan2TeamNo={match.clan2TeamNo}
            defaultScheduledAt={match.scheduledAt}
            compTeamSize={comp?.teamSize}
            // F4: the card's format comes from the COMPETITION, not a per-match
            // dropdown. See MatchRosterInitPanel's formatFromSeriesLength.
            compSeriesLength={comp?.seriesFormat?.length}
            compName={comp?.name}
          />
        </ManualPickerDisclosure>
      )}
      {status === 'DRAFT' && !isAdmin && !isSideRunner && (
        <p className="rounded-xl border border-iron-700 bg-iron-900 px-4 py-6 text-center font-mono text-xs text-iron-500">
          This match card hasn't been set up yet — waiting on a clan leader or admin to build the roster.
        </p>
      )}

      {/* ── Map veto strip ──────────────────────────────────────────────── */}
      {/* id anchors below are the scroll targets for the Verification strip's
          items (lib/matchVerifyChecklist.ts's sectionId). Plain wrappers — no
          layout change, they exist only to be findable by getElementById. */}
      {format !== 'LEGACY' && (
        <div ref={highlight.registerSection('match-veto')} className={cn('rounded-xl', highlight.isHighlighted('match-veto') && 'section-pulse')}>
        <VetoBoard
          veto={match.mapVeto}
          guide={data.vetoGuide}
          myClanId={effectiveMe?.discordId === match.leader1Id ? match.clan1 : effectiveMe?.discordId === match.leader2Id ? match.clan2 : undefined}
          clan1Id={match.clan1}
          clan2Id={match.clan2}
          clan1Label={clan1Label}
          clan2Label={clan2Label}
          gameModes={gameModes}
          status={status}
          matchId={matchId}
          compId={compId}
          // Captain widen: the person running the team on the night gets a say
          // in the maps it plays. Was leader/admin only, so a captain whose
          // leader was away watched it auto-pick at T-10.
          canAct={!!me && (isSideRunner || isAdmin)}
          isAdmin={isAdmin}
          compName={comp?.name}
          cardBranding={data.vetoCardBranding}
        />
        </div>
      )}

      {/* ── Slot grid ───────────────────────────────────────────────────── */}
      {format !== 'LEGACY' && gameModes.length > 0 && (
        <div ref={highlight.registerSection('match-slots')} className={cn('rounded-xl', highlight.isHighlighted('match-slots') && 'section-pulse')}>
        <SlotGrid
          slotsByGame={slotsByGame}
          gameModes={gameModes}
          summary={summary}
          mapVeto={match.mapVeto}
          status={status}
          scheduledAt={match.scheduledAt}
          matchId={matchId}
          compId={compId}
          me={effectiveMeProp}
          clan1Id={match.clan1}
          clan2Id={match.clan2}
          clan1TeamNo={match.clan1TeamNo}
          clan2TeamNo={match.clan2TeamNo}
          clan1Label={clan1Label}
          clan2Label={clan2Label}
          clan1Roster={match.clan1Display ?? match.clan1RosterSnapshot ?? []}
          clan2Roster={match.clan2Display ?? match.clan2RosterSnapshot ?? []}
          clan1Snap={match.clan1RosterSnapshot ?? []}
          clan2Snap={match.clan2RosterSnapshot ?? []}
          isAdmin={isAdmin}
          // Per-SIDE now: a captain/leader reviews their OWN side's shots only.
          // The server enforces the same boundary per slot.
          canInspect={canInspect1 || canInspect2}
          canInspectSide1={canInspect1}
          canInspectSide2={canInspect2}
          isLeader1={isLeader1}
          isLeader2={isLeader2}
          canFlagOrgManager={canManageMatchOrg}
          // Delegation-visibility audit (T3): defensive ?? {} since older
          // fixtures / the pre-contract backend may not send this field yet.
          // Reads the view-as-sanitised local, NOT data.viewerCaptainOf —
          // SlotGrid reinstates poke + proxy-upload rights from this via
          // canPokeSide/canProxyUploadSide, so the raw field here would have
          // walked the captain leg straight back in past the gate above.
          viewerCaptainOf={viewerCaptainOf ?? {}}
        />
        </div>
      )}

      {/* ── OCR digestion trigger + live watch (org-manager/founder only) ── */}
      {/* Gated by canOperateDigestion — NOT isAdmin/isLeader. This is the
          portal-tier org-role boundary the backend actually enforces on
          run-ocr-batch/ocrfeed (canOperateOcrDigestion in matchCardV2.ts),
          deliberately distinct from the Discord-native admin flag
          used everywhere else on this page. Visibility window mirrors
          ScoreboardConsensus's own CLOSED/OCR/VERIFIED/FORGED gate above,
          narrowed to the states where triggering-or-watching a run actually
          makes sense: CLOSED/DISPUTED (clickable "run"), OCR (processing/
          live-watch). Server has no cardStatus gate at all on run-ocr-batch —
          this window is client-side UX polish only. */}
      {format !== 'LEGACY' && canOperateDigestion && (status === 'CLOSED' || status === 'DISPUTED' || status === 'OCR') && (
        <div ref={highlight.registerSection('match-digestion')} className={cn('rounded-xl', highlight.isHighlighted('match-digestion') && 'section-pulse')}>
        <OcrDigestionPanel
          data={data}
          matchId={matchId}
          compId={compId}
          status={status}
          clan1={clan1}
          clan2={clan2}
        />
        </div>
      )}

      {/* ── Score consensus + admin cleanup editor + flag button ───────── */}
      {format !== 'LEGACY' && (status === 'CLOSED' || status === 'OCR' || status === 'VERIFIED' || status === 'FORGED') && (
        <ScoreboardConsensus
          slotsByGame={slotsByGame}
          gameModes={gameModes}
          matchId={matchId}
          compId={compId}
          clan1Id={match.clan1}
          clan2Id={match.clan2}
          clan1Label={clan1Label}
          clan2Label={clan2Label}
          clan1Roster={(match as { clan1Display?: string[]; clan1RosterSnapshot?: string[] }).clan1Display ?? (match as { clan1RosterSnapshot?: string[] }).clan1RosterSnapshot ?? []}
          clan2Roster={(match as { clan2Display?: string[]; clan2RosterSnapshot?: string[] }).clan2Display ?? (match as { clan2RosterSnapshot?: string[] }).clan2RosterSnapshot ?? []}
          clan1Snap={(match as { clan1RosterSnapshot?: string[] }).clan1RosterSnapshot ?? []}
          clan2Snap={(match as { clan2RosterSnapshot?: string[] }).clan2RosterSnapshot ?? []}
          canEdit={canManageScores}
          canFlag={!!me}
          isAdmin={isAdmin}
          serverConsensus={match.ocrBatchSummary?.consensus}
        />
      )}

      {/* ── Confirm scores (Arc 3, scoring unification) — the missing wire:
          derives games[]/nightWinner from the OCR'd slots above and writes
          status CONFIRMED, so this match finally reaches standings/R2/R4.
          Same visibility window as ScoreboardConsensus minus FORGED (once
          forged the card is public/final — re-deriving from here would be
          confusing, use the admin transition/dispute path instead). Auth is
          real-enforced server-side (admin, org-manager, or comp-manager
          — matchCardV2.ts's handleConfirmScores); `canManageScores` here is
          client-side UX gating only (org-admin de-gate: widened from
          `isAdmin` alone to also admit an org manager of THIS comp's own
          org — see canManageScores above), same posture every other
          admin-only control on this page already takes. */}
      {/* ── Result sign-off (stage 2) — sits ABOVE Confirm Scores deliberately:
          it is the step that precedes it. Same status window as the scoreboard
          it signs off, minus the terminal states where sign-off is frozen. */}
      {format !== 'LEGACY' && (status === 'CLOSED' || status === 'OCR' || status === 'DISPUTED') && (
        <div ref={highlight.registerSection('match-signoff')} className={cn('rounded-xl', highlight.isHighlighted('match-signoff') && 'section-pulse')}>
          <SignoffPanel
            matchId={matchId}
            compId={compId}
            signoff={data.signoff}
            clan1Label={clan1Label}
            clan2Label={clan2Label}
            canSign1={canSign1}
            canSign2={canSign2}
            myDiscordId={effectiveMe?.discordId}
            // "Something's wrong" points at the EXISTING per-cell flag buttons
            // on the scoreboard rather than opening a second dispute path.
            onFlagRequested={() => highlight.highlightNow('match-slots')}
          />
        </div>
      )}

      {format !== 'LEGACY' && canManageScores && (status === 'CLOSED' || status === 'OCR' || status === 'VERIFIED') && (
        <div ref={highlight.registerSection('match-confirm')} className={cn('rounded-xl', highlight.isHighlighted('match-confirm') && 'section-pulse')}>
          <ConfirmScoresPanel matchId={matchId} compId={compId} match={match} />
        </div>
      )}

      {/* ── Forged result (preview) ─────────────────────────────────────── */}
      {(status === 'VERIFIED' || status === 'FORGED') && (
        <ForgedPanel matchId={matchId} forgedCardUrl={match.forgedCardUrl} status={status} compId={compId} canForge={isAdmin} match={match} />
      )}

      {/* ── Score-flag inbox — org-admin de-gate (2026-07-17): widened from
          `isAdmin` alone to also admit an org manager (ORG_ADMIN/ORG_OWNER)
          of THIS comp's own org (`canManageScores` above) — mirrors the
          backend's handleResolveScoreFlag/handleListScoreFlags widen
          (matchCardV2.ts). Same posture as ConfirmScoresPanel above: real
          enforcement is server-side, this is UX gating only. */}
      {canManageScores && status !== 'FORGED' && status !== 'CANCELLED' && (
        <div ref={highlight.registerSection('match-flags')} className={cn('rounded-xl', highlight.isHighlighted('match-flags') && 'section-pulse')}>
          <ScoreFlagInbox matchId={matchId} compId={compId} />
        </div>
      )}

      {/* ── Admin transition ────────────────────────────────────────────── */}
      {/* ── Admin transition ──────────────────────────────────────────────
          Gated on canManageScores, NOT isAdmin (F14/audit, 2026-07-22).
          Widened deliberately: with the helper tier benched, `isAdmin` alone
          meant the FOUNDER was the only person on the platform who could
          finish a match — while the ORG_OWNER who actually runs the comp could
          do every preceding step (run OCR, fix the scoreboard, clear flags,
          confirm scores) and then hit a missing button. Lisa's ruling: org
          owners and admins are valid operators. Mirrors the backend's new
          canTransitionTo (shared/matchTransitionAuthz.ts) — real enforcement is
          server-side, this is UX gating only, same posture as every other
          control on this page. Leaders still get no bar: their legitimate
          transitions (CLOSED/DISPUTED) deserve a participant-facing control,
          not an operator bar — that's stage 2. */}
      {canManageScores && status !== 'FORGED' && status !== 'CANCELLED' && (
        <div ref={highlight.registerSection('match-transition')} className={cn('rounded-xl', highlight.isHighlighted('match-transition') && 'section-pulse')}>
          <AdminTransitionBar matchId={matchId} compId={compId} status={status} match={match} />
        </div>
      )}

      {/* Participant status bar (Lisa, 2026-07-22) — a side's leader or its
          team captain reports on their own game: "we've finished playing",
          "I dispute this", and the run-up to kick-off. Narrowed to exactly the
          targets shared/matchTransitionAuthz.ts allows them, so there are no
          buttons here that 403. NOT a correctness statement — CLOSED just means
          the games are over; confirming the RESULT is the sign-off panel. */}
      {showParticipantBar && status !== 'FORGED' && status !== 'CANCELLED' && (
        <div ref={highlight.registerSection('match-transition')} className={cn('rounded-xl', highlight.isHighlighted('match-transition') && 'section-pulse')}>
          <AdminTransitionBar matchId={matchId} compId={compId} status={status} match={match} participant />
        </div>
      )}

      {/* ── Comments thread ─────────────────────────────────────────────── */}
      <CommentsThread matchId={matchId} compId={compId} comments={comments} me={effectiveMeProp} isAdmin={isAdmin} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Manual-picker disclosure (Lisa, 2026-07-23) — see the call site's comment.
// Open by default ONLY when the server has stamped that the squad cannot be
// derived; otherwise a one-line escape hatch that says what it is FOR, so it
// stops reading as "the button that makes the match card".
function ManualPickerDisclosure({ needed, why, children }: {
  needed: boolean; why?: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(needed);
  // The stamp can land while the page is open (the agreement completes, the
  // build tries, derivation fails) — unfold when it does, and never re-fold
  // something the operator has opened.
  useEffect(() => { if (needed) setOpen(true); }, [needed]);

  if (open) {
    return (
      <div>
        {needed && (
          <p className="mb-2 font-mono text-[11px] text-iron-400">
            This one needs a human: {why || 'a side has more seats than the competition allows'}. Pick the squads below.
          </p>
        )}
        {children}
      </div>
    );
  }

  return (
    <button
      onClick={() => setOpen(true)}
      className="w-full rounded-xl border border-iron-800 px-4 py-3 text-left font-mono text-[11px] text-iron-500 hover:border-iron-700 hover:text-iron-400"
    >
      Squads can't be worked out automatically? Build the card by hand instead →
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Match agreement gate (2026-07-17): the opponent captain (match.leader2Id)
// must Agree or Decline a captain-proposed match before it goes PENDING and
// is real. Real enforcement is server-side (assertMayRespondToMatch in
// shared/matchAgreement.ts — proposer 403s on their own match, unrelated
// users 403, only leader2 or a founder/admin override may pass); this banner
// is client-side UX: it shows action buttons ONLY to the opponent captain,
// and a read-only wait state to everyone else, so no one is offered a click
// the server will refuse.
function AgreementBanner({ matchId, compId, match, isLeader1, isLeader2, clan1Label, clan2Label }: {
  matchId: string; compId: string;
  match: MatchCardV2Response['match'];
  isLeader1: boolean; isLeader2: boolean;
  clan1Label: string; clan2Label: string;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
  const agreeMutation = useMutation({
    mutationFn: () => api.agreeMatch(matchId, compId),
    onSuccess: () => { setError(''); invalidate(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const declineMutation = useMutation({
    mutationFn: () => api.declineMatch(matchId, compId),
    onSuccess: () => { setError(''); invalidate(); },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const pending = agreeMutation.isPending || declineMutation.isPending;
  const scheduledAt = match.scheduledAt as number | undefined;

  // Opponent captain — the only actor this UI offers a click to.
  if (isLeader2) {
    return (
      <div className="rounded-xl border-2 border-amber-500/60 bg-amber-950/30 px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-lg font-extrabold uppercase tracking-wider text-amber-200">
              Agree to this match?
            </div>
            <p className="mt-1 font-mono text-xs text-amber-100/80">
              {clan1Label} vs {clan2Label}
              {' · '}{scheduledAt ? `${formatDateTime(scheduledAt)} (${timeUntil(scheduledAt)})` : 'No scheduled time'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => agreeMutation.mutate()}
              disabled={pending}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              <Check size={14} /> {agreeMutation.isPending ? 'Agreeing…' : 'Agree'}
            </button>
            <button
              type="button"
              onClick={() => declineMutation.mutate()}
              disabled={pending}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-600/60 bg-red-950/40 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-red-200 hover:bg-red-900/50 disabled:opacity-50"
            >
              <X size={14} /> {declineMutation.isPending ? 'Declining…' : 'Decline'}
            </button>
          </div>
        </div>
        {error && <p className="mt-2 font-mono text-xs text-red-300">{error}</p>}
      </div>
    );
  }

  // Proposer or bystander — read-only wait state.
  return (
    <div className="rounded-xl border border-iron-700 bg-iron-900 px-5 py-4">
      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-iron-400">
        <Hourglass size={14} />
        {isLeader1 ? `Waiting for ${clan2Label} to agree.` : "Awaiting the captains' agreement."}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ClanPlate({ clanId, label, roster, names, side }: {
  // Canonical (CLAN#-stripped) id — drives the /clan/:name route. NEVER
  // swap this for the formatted `label` below; the clan detail page resolves
  // by canonical id, not by a spaced-out/branded display string.
  clanId: string;
  // Human-visible name only (clanLabelFrom-formatted) — display text, never
  // used for routing/lookups.
  label: string;
  roster?: string[];
  // Parallel array (same index as `roster`) of resolved display names — a
  // missing/undefined entry falls back to a truncated Discord id, never the
  // raw id itself.
  names?: Array<string | undefined>;
  side: 'left' | 'right';
}) {
  return (
    <Link
      to={`/clan/${encodeURIComponent(clanId)}`}
      className={cn(
        'group rounded-xl border-2 border-iron-700 bg-iron-900 p-5 transition-all hover:border-forge-500/60 hover:bg-iron-800',
        side === 'right' && 'text-right',
      )}
    >
      <div className="font-display text-2xl font-extrabold uppercase tracking-wider text-white group-hover:text-forge-300">
        {label}
      </div>
      <div className="mt-1 font-mono text-xs text-iron-400">
        {roster?.length ?? 0} players locked
      </div>
      {roster && roster.length > 0 && (
        <div className={cn('mt-3 flex flex-wrap gap-1', side === 'right' && 'justify-end')}>
          {roster.slice(0, 8).map((pid, i) => (
            <span key={pid} className="inline-flex items-center rounded-md bg-iron-800 px-2 py-1 font-mono text-[11px] text-iron-300" title={names?.[i] ? `${names[i]} (${pid})` : pid}>
              {names?.[i] ?? `…${pid.slice(-4)}`}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function SlotGrid({ slotsByGame, gameModes, summary, mapVeto, status, scheduledAt, matchId, compId, me, clan1Id, clan2Id, clan1TeamNo, clan2TeamNo, clan1Label, clan2Label, clan1Roster, clan2Roster, clan1Snap, clan2Snap, isAdmin, canInspect, canInspectSide1, canInspectSide2, isLeader1, isLeader2, canFlagOrgManager, viewerCaptainOf }: {
  slotsByGame: Record<number, SubmissionSlotRow[]>;
  gameModes: string[];
  summary: MatchCardV2Response['summary'];
  mapVeto?: MapVetoState;
  status: MatchCardStatus;
  // Match-night submission window (P2, 2026-07-25) — threaded down to
  // SlotChip so it can decide "is the window open" the same way the server
  // does (submissionWindowOpen, ../lib/submissionWindow.ts) rather than the
  // old `status === 'LIVE'`-only check.
  scheduledAt?: number;
  matchId: string; compId: string;
  me?: Me;
  clan1Id: string; clan2Id: string;
  // Delegation-visibility audit (T3) — each side's own team-entry number
  // (match.clan1TeamNo/clan2TeamNo). Undefined for a legacy/non-team side —
  // isMatchSideCaptain treats that the same way the backend's
  // isActiveSideCaptain does: never captain-eligible.
  clan1TeamNo?: number; clan2TeamNo?: number;
  clan1Label?: string; clan2Label?: string;
  clan1Roster: Array<string | undefined>;
  clan2Roster: Array<string | undefined>;
  clan1Snap: string[];
  clan2Snap: string[];
  isAdmin: boolean;
  /** Per-SIDE screenshot review (Lisa, 2026-07-22): a side's leader or its team
   *  captain may open THEIR OWN side's shots and not the opponent's. Undefined
   *  falls back to the whole-grid `canInspect` — so an operator, who may open
   *  both, is unchanged. Server enforces the same boundary per slot. */
  canInspectSide1?: boolean;
  canInspectSide2?: boolean;
  // Score-inspect widen (org-admin de-gate): defaults to isAdmin when the
  // caller doesn't pass it, so nothing else that renders this component
  // changes behaviour — only CardBody passes the widened canManageScores.
  canInspect?: boolean;
  isLeader1: boolean; isLeader2: boolean;
  // Raise-flag widen (org-admin de-gate, 2026-07-17): an org manager
  // (ORG_ADMIN/ORG_OWNER) of THIS comp's own org may raise a flag even when
  // not a match participant — mirrors the backend's handleScoreFlag widen
  // (matchCardV2.ts). Defaults to false (existing behaviour) when a caller
  // doesn't pass it.
  canFlagOrgManager?: boolean;
  // Delegation-visibility audit (T3) — teamNos the viewer captains, keyed by
  // raw clan id (clan1Id/clan2Id). Defaults to {} when a caller doesn't pass
  // it (older fixtures) — see lib/matchCaptainAccess.ts.
  viewerCaptainOf?: Record<string, number[]>;
}) {
  const resolvedCanInspect = canInspect ?? isAdmin;
  const resolvedCanFlagOrgManager = canFlagOrgManager ?? false;
  const games = Array.from({ length: gameModes.length }, (_, i) => i + 1);

  // GOD-policy L7b — poke eligibility + pending counts per side. REVISED
  // 2026-07-21 (Lisa product ruling, overrides the shipped L7b design): a
  // poke on side N is now an OWN-SIDE nudge, clickable by THAT SAME side's
  // leader, captain, or admin — mirrors the backend's handlePoke gate
  // exactly (isSideLeader || isSideCaptain || admin trio), via the SAME pure
  // helper matchCaptainAccess.ts uses to unit-test this. Cross-side poking
  // no longer exists. A delegated captain who isn't also the clan leader
  // still sees this button when the server would accept them.
  const allSlots = useMemo(() => Object.values(slotsByGame).flat(), [slotsByGame]);
  const pendingCount1 = allSlots.filter(s => s.clanId === clan1Id && s.status === 'PENDING').length;
  const pendingCount2 = allSlots.filter(s => s.clanId === clan2Id && s.status === 'PENDING').length;
  const canPokeSide1 = canPokeSide({
    status, pendingCount: pendingCount1, isAdmin, isSideLeader: isLeader1,
    viewerCaptainOf, sideClanKey: clan1Id, sideTeamNo: clan1TeamNo,
  });
  const canPokeSide2 = canPokeSide({
    status, pendingCount: pendingCount2, isAdmin, isSideLeader: isLeader2,
    viewerCaptainOf, sideClanKey: clan2Id, sideTeamNo: clan2TeamNo,
  });
  // Proxy-upload is own-clan, own-team only for captains (backend 403s both
  // the cross-clan AND cross-team case) — same helper canDeclare in SlotChip
  // reuses transitively via canProxyUpload (see its own doc comment).
  const canProxyUpload1 = canProxyUploadSide({
    isAdmin, isSideLeader: isLeader1, viewerCaptainOf, sideClanKey: clan1Id, sideTeamNo: clan1TeamNo,
  });
  const canProxyUpload2 = canProxyUploadSide({
    isAdmin, isSideLeader: isLeader2, viewerCaptainOf, sideClanKey: clan2Id, sideTeamNo: clan2TeamNo,
  });

  // Build a playerId → display gamertag map. The roster snapshot array
  // carries the slot-identity (Discord IDs in prod, synthetic IDs in
  // demos); the display array carries the real gamertag at the same index.
  // So index N in clan1Snap corresponds to clan1Roster[N].
  const playerIdToDisplay = useMemo(() => {
    const m = new Map<string, string>();
    for (let i = 0; i < clan1Snap.length; i++) {
      const id = clan1Snap[i]; const name = clan1Roster[i];
      if (id && name) m.set(id, name);
    }
    for (let i = 0; i < clan2Snap.length; i++) {
      const id = clan2Snap[i]; const name = clan2Roster[i];
      if (id && name) m.set(id, name);
    }
    return m;
  }, [clan1Snap, clan2Snap, clan1Roster, clan2Roster]);

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
          <Upload size={16} /> Submission Slots
        </h3>
        <div className="font-mono text-xs text-iron-400">
          <span className="font-bold text-forge-300">{summary.slotsDone}</span> done ·
          <span className="ml-2 text-iron-300">{summary.slotsSubmitted}</span> in ·
          <span className="ml-2">{summary.slotsExpected} total</span>
          {summary.slotsMissed > 0 && <span className="ml-2 text-red-400">{summary.slotsMissed} missed</span>}
          {summary.slotsFlagged > 0 && <span className="ml-2 text-amber-400">{summary.slotsFlagged} flagged</span>}
        </div>
      </div>

      {/* Legend — what the chip colours mean */}
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-iron-500">
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-700" /> done</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-blue-700" /> uploaded</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-iron-600" /> waiting</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-amber-700" /> flagged / disputed</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-red-700" /> missed / excluded</span>
        <span className="text-iron-500">·</span>
        <span><span className="text-amber-300">!</span> disputed · <span className="text-orange-300">⚠</span> stage-2 reject · <span className="text-pink-400">⇄</span> strip/slot mismatch · <span className="text-fuchsia-400">≢</span> Activision ID mismatch · <span className="text-red-300">⛔</span> excluded</span>
      </div>

      {/* Rows-by-player / columns-by-mode (Lisa, 2026-07-13): replaces the
          previous per-GAME layout (one section per game, each with two
          team rows of slot chips) with one table per team — players down
          the left, one column per game. The old layout scattered up to
          24 upload buttons across N per-game sections; images kept landing
          in the wrong game's slots because nothing on screen distinguished
          "G1 upload" from "G2 upload" beyond a small section header above.
          A table makes the destination of every button visually
          unambiguous: the row says WHO, the column says WHICH GAME. */}
      <div className="space-y-4">
        {/* Proxy-upload is own-clan only for leaders (backend 403s the
            cross-clan case); flagging stays open to BOTH leaders — it is
            the dispute channel against the opposing team's evidence.
            Org-admin de-gate (2026-07-17): canFlag ALSO admits an org
            manager (ORG_ADMIN/ORG_OWNER) of THIS comp's own org
            (canManageMatchOrg), even when not a match participant — mirrors
            the backend's handleScoreFlag widen (matchCardV2.ts). */}
        <ClanSlotTable
          label={clanLabelFrom(clan1Label, clan1Id)}
          clanId={clan1Id}
          roster={clan1Snap}
          games={games}
          gameModes={gameModes}
          slotsByGame={slotsByGame}
          mapVeto={mapVeto}
          playerIdToDisplay={playerIdToDisplay}
          matchId={matchId} compId={compId} status={status} scheduledAt={scheduledAt} me={me}
          canFlag={isAdmin || isLeader1 || isLeader2 || resolvedCanFlagOrgManager}
          canProxyUpload={canProxyUpload1}
          canInspect={canInspectSide1 ?? resolvedCanInspect}
          canPoke={canPokeSide1}
          targetSide="1"
        />
        <ClanSlotTable
          label={clanLabelFrom(clan2Label, clan2Id)}
          clanId={clan2Id}
          roster={clan2Snap}
          games={games}
          gameModes={gameModes}
          slotsByGame={slotsByGame}
          mapVeto={mapVeto}
          playerIdToDisplay={playerIdToDisplay}
          matchId={matchId} compId={compId} status={status} scheduledAt={scheduledAt} me={me}
          canFlag={isAdmin || isLeader1 || isLeader2 || resolvedCanFlagOrgManager}
          canProxyUpload={canProxyUpload2}
          canInspect={canInspectSide2 ?? resolvedCanInspect}
          canPoke={canPokeSide2}
          targetSide="2"
        />
      </div>
    </section>
  );
}

// One clan's submission table — rows are the clan's roster-snapshot player
// ids (the slot-identity array, stable across the whole match), columns are
// the match's games (BO5/BO7 → 5/7 columns, never hardcoded to 3). Each cell
// is the SAME SlotChip used before this wave — its status colours, flags,
// proxy-upload button, and inspect click-through are untouched, only WHERE
// it's placed on screen changed.
function ClanSlotTable({ label, clanId, roster, games, gameModes, slotsByGame, mapVeto, playerIdToDisplay, matchId, compId, status, scheduledAt, me, canFlag, canProxyUpload, canInspect, canPoke, targetSide }: {
  label: string;
  clanId: string;
  roster: string[];
  games: number[];
  gameModes: string[];
  slotsByGame: Record<number, SubmissionSlotRow[]>;
  mapVeto?: MapVetoState;
  playerIdToDisplay: Map<string, string>;
  matchId: string; compId: string; status: MatchCardStatus; scheduledAt?: number; me?: Me;
  canFlag: boolean; canProxyUpload: boolean; canInspect: boolean;
  // GOD-policy L7b — REVISED 2026-07-21 (own-side nudge): true when the
  // CURRENT viewer is eligible to poke THIS side (they represent THIS same
  // side — its leader or active team captain — or are admin) AND this side
  // has >=1 PENDING slot right now. targetSide is this table's own side
  // number, passed straight through to api.matchCardPoke.
  canPoke: boolean;
  targetSide: '1' | '2';
}) {
  // Pivot: this clan's slot for (gameNum, playerId) — scoped by clanId so a
  // synthetic ghost id shared across demo fixtures can never cross-match the
  // opposing team's cell.
  const slotFor = (gameNum: number, playerId: string): SubmissionSlotRow | undefined =>
    (slotsByGame[gameNum] ?? []).find(s => s.playerId === playerId && s.clanId === clanId);

  return (
    <div className="rounded-lg border border-iron-700 bg-iron-950 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="font-display text-xs font-bold uppercase tracking-wider text-iron-200">{label}</div>
        {canPoke && <PokeButton matchId={matchId} compId={compId} targetSide={targetSide} />}
      </div>
      {roster.length === 0 ? (
        <p className="font-mono text-[11px] text-iron-500">No roster snapshot.</p>
      ) : (
        // Mobile: this table's own scroll container — the page body must
        // never scroll sideways, only this element does when a BO7's 7
        // columns don't fit a phone screen.
        <div className="overflow-x-auto">
          <table className="w-full border-collapse font-mono text-[11px]">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 whitespace-nowrap bg-iron-950 px-2 py-1 text-left font-display text-[9px] uppercase tracking-wider text-iron-500">
                  Player
                </th>
                {games.map(g => {
                  const mode = gameModes[g - 1];
                  const map = mapVeto?.picks.find(p => p.gameNum === g)?.mapName;
                  return (
                    <th key={g} className="whitespace-nowrap px-2 py-1 text-left font-display text-[9px] uppercase tracking-wider text-iron-500">
                      <div>G{g} · {MODE_LABEL(mode)}</div>
                      {map && <div className="mt-0.5 font-mono text-[9px] normal-case tracking-normal text-iron-600">{map}</div>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {roster.map(playerId => (
                <tr key={playerId} className="border-t border-iron-800">
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-iron-950 px-2 py-1.5 text-iron-200">
                    {playerIdToDisplay.get(playerId) ?? `…${playerId.slice(-4)}`}
                  </td>
                  {games.map(g => {
                    const slot = slotFor(g, playerId);
                    return (
                      <td key={g} className="px-2 py-1.5">
                        {slot ? (
                          <SlotChip
                            slot={slot}
                            matchId={matchId} compId={compId} status={status} scheduledAt={scheduledAt} me={me}
                            canFlag={canFlag} canProxyUpload={canProxyUpload}
                            displayName={playerIdToDisplay.get(playerId)}
                            playerIdToDisplay={playerIdToDisplay}
                            canInspect={canInspect}
                          />
                        ) : (
                          <span className="font-mono text-[11px] text-iron-600">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// GOD-policy L7b, Piece 1 — REVISED 2026-07-21 (Lisa product ruling,
// overrides the shipped L7b design): an OWN-SIDE nudge for outstanding
// screenshots — "each house chases its own." Only rendered when the viewer
// is eligible (ClanSlotTable's canPoke — THIS side's leader/captain, or
// admin) AND this side has >=1 PENDING slot. Local rate-limit state is
// session-only (nextAllowedAt isn't threaded back through GET /matchcard) —
// good enough to explain "why is this disabled" right after a click; a
// fresh page load simply re-enables the button and lets the server's own
// atomic conditional Put be the real gate (a stale-looking enabled button
// still 429s honestly with a fresh countdown).
function PokeButton({ matchId, compId, targetSide }: { matchId: string; compId: string; targetSide: '1' | '2' }) {
  const queryClient = useQueryClient();
  const [nextAllowedAt, setNextAllowedAt] = useState<number | null>(null);
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.matchCardPoke(matchId, compId, targetSide),
    onSuccess: (res) => {
      setError('');
      setNextAllowedAt(res.nextAllowedAt);
      void queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
    onError: (e: unknown) => {
      const parsed = parseApiError(e);
      const raw = parsed.raw as { nextAllowedAt?: number } | null;
      if (parsed.code === 'poke_rate_limited' && typeof raw?.nextAllowedAt === 'number') {
        setNextAllowedAt(raw.nextAllowedAt);
        setError('');
      } else {
        setError(parsed.message);
      }
    },
  });

  const now = Date.now();
  const rateLimited = !!nextAllowedAt && nextAllowedAt > now;
  const hoursLeft = rateLimited ? Math.max(1, Math.ceil((nextAllowedAt! - now) / (60 * 60 * 1000))) : 0;

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending || rateLimited}
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 font-mono text-[10px]',
          rateLimited ? 'cursor-not-allowed bg-iron-800 text-iron-500' : 'bg-forge-600 text-white hover:bg-forge-500',
        )}
        title={rateLimited
          ? `Poked — ${hoursLeft}h until you can poke again`
          : 'Nudge your team for their outstanding screenshots'}
      >
        <Bell size={10} />
        {rateLimited ? `Poked — ${hoursLeft}h left` : mutation.isPending ? 'Poking…' : 'Poke your team for screenshots'}
      </button>
      {error && <span className="font-mono text-[10px] text-red-400">{error}</span>}
    </div>
  );
}

// Roster-integrity flag label (Lisa product ruling, 2026-07-21) — pure so
// MatchCardLive.test.ts can cover all three notRosteredWho values without a
// full component render. Observational only; never implies the submission
// was blocked (flags observe, gates stay as they are today).
export function notRosteredLabel(slot: { notRostered?: boolean; notRosteredWho?: 'submitter' | 'slotOwner' | 'both' }): string | null {
  if (!slot.notRostered) return null;
  if (slot.notRosteredWho === 'slotOwner') return 'NOT ON ROSTER — slot owner';
  if (slot.notRosteredWho === 'both') return 'NOT ON ROSTER — submitter + slot owner';
  return 'NOT ON ROSTER — submitter';
}

// Exported for MatchCardLive.test.ts (map-completeness tests).
export const SLOT_BADGE: Record<SlotStatus, { bg: string; icon: React.ReactNode; text: string; label: string }> = {
  PENDING:        { bg: 'bg-iron-700',     icon: <Hourglass size={10} />, text: 'text-iron-300',     label: 'PENDING' },
  UPLOADED:       { bg: 'bg-blue-800',     icon: <Upload size={10} />,    text: 'text-blue-200',     label: 'UPLOADED' },
  OCR_PROCESSING: { bg: 'bg-blue-700',     icon: <Hourglass size={10} className="animate-pulse" />, text: 'text-blue-100', label: 'OCR' },
  OCR_DONE:       { bg: 'bg-emerald-800',  icon: <Check size={10} />,     text: 'text-emerald-200',  label: 'DONE' },
  MISSED:         { bg: 'bg-red-900/60',   icon: <AlertTriangle size={10} />, text: 'text-red-300', label: 'MISSED' },
  FLAGGED:        { bg: 'bg-amber-800',    icon: <AlertTriangle size={10} />, text: 'text-amber-200', label: 'FLAG' },
};

function SlotChip({ slot, matchId, compId, status, scheduledAt, me, canFlag, canProxyUpload, displayName, canInspect, playerIdToDisplay }: {
  slot: SubmissionSlotRow; matchId: string; compId: string; status: MatchCardStatus;
  // Match-night submission window (P2, 2026-07-25) — see slotIsOpen below.
  scheduledAt?: number;
  me?: Me;
  canFlag: boolean; canProxyUpload: boolean;
  displayName?: string;
  canInspect: boolean;
  playerIdToDisplay?: Map<string, string>;
}) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [inspecting, setInspecting] = useState(false);
  // GOD-policy L7b — inline "nothing to submit" confirm (no browser prompt()).
  const [declaring, setDeclaring] = useState(false);
  const [declareReason, setDeclareReason] = useState('');

  // Same fallback discipline as themeFor: an unmapped slot status renders a
  // neutral chip labelled with the raw value instead of crashing the card.
  const badge = SLOT_BADGE[slot.status] ?? {
    bg: 'bg-iron-800', icon: <AlertTriangle size={10} />, text: 'text-iron-300', label: String(slot.status),
  };
  const isMine = me?.discordId === slot.playerId;
  // Match-night submission window (P2, 2026-07-25 widening): used to be
  // `status === 'LIVE'` only. A card is now LIVE for the whole T0..T+24h
  // span (P1), but submissions ALSO open one hour before kickoff while the
  // card is still SCHEDULED/VETO — submissionWindowOpen (../lib/
  // submissionWindow.ts, a byte-identical mirror of the backend's
  // src/shared/submissionWindow.ts — see that file for why this frontend
  // package can't just import the real one) decides the same way the
  // server's handleSlotUploadUrl/handleSlotConfirm/handleDeclareNothing do.
  // GO_LIVE itself is unchanged (still fires at T0) — only this GATE moved
  // earlier, never the phase ladder.
  const slotIsOpen = submissionWindowOpen(status, scheduledAt, Date.now())
    && (slot.status === 'PENDING' || slot.status === 'UPLOADED');
  // canUpload's scope (self, or own-side captain/leader/admin via
  // canProxyUpload) is EXACTLY the same authorization handleDeclareNothing
  // enforces server-side, so "can I declare this PENDING slot" reuses it
  // as-is rather than threading a second identical boolean down.
  const canUpload = slotIsOpen && (isMine || canProxyUpload);
  const canDeclare = canUpload && slot.status === 'PENDING';
  const isProxy = canUpload && !isMine;

  const declareMutation = useMutation({
    mutationFn: (reason: string) => api.slotDeclareNothing(matchId, slot.gameNum, slot.playerId, compId, reason || undefined),
    onSuccess: () => {
      setDeclaring(false);
      setDeclareReason('');
      void queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
    onError: (e: unknown) => setError(parseApiError(e).message),
  });

  // Honest, restrained provenance line for a MISSED slot — no colour
  // strips, just typography (no_severity_highlight_strips convention).
  // Absent missedVia means a legacy row from before this field existed —
  // renders nothing extra, same as today.
  const declaredByName = slot.declaredBy ? (playerIdToDisplay?.get(slot.declaredBy) ?? `…${slot.declaredBy.slice(-4)}`) : undefined;
  const missedDetail: string | null = slot.status !== 'MISSED' || !slot.missedVia ? null
    : slot.missedVia === 'timeout' ? 'Timed out — no submission received'
    : slot.missedVia === 'self-declared' ? `No submission — self-declared${slot.reason ? ` (${slot.reason})` : ''}`
    : slot.missedVia === 'admin-declared' ? `No submission — admin-declared${declaredByName ? ` by ${declaredByName}` : ''}${slot.reason ? ` (${slot.reason})` : ''}`
    : `No submission — captain-declared${declaredByName ? ` by ${declaredByName}` : ''}${slot.reason ? ` (${slot.reason})` : ''}`;

  // Roster-integrity flag (Lisa product ruling, 2026-07-21) — observational
  // only, typography-only tag (no colour strip, per no_severity_highlight_strips).
  // Captains keep their current team-assignment reach; this just tells a
  // reviewer the submitter (and/or slot owner, for a proxy) no longer held
  // clan membership at submission time — it never changes who was allowed
  // to submit. Logic lives in the exported notRosteredLabel (see above) so
  // MatchCardLive.test.ts can cover it without a full component render.
  const notRosteredTag = notRosteredLabel(slot);

  // Quality flags persisted on the slot row
  const qualityFlags = (slot as unknown as { qualityFlags?: ShotQualityFlag[] }).qualityFlags ?? [];
  const excluded = (slot as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus ?? false;

  const flagMutation = useMutation({
    mutationFn: ({ flag, remove }: { flag: ShotQualityFlag; remove: boolean }) =>
      api.shotFlag(matchId, slot.gameNum, slot.playerId, compId, flag, { remove }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
  });

  const handleUpload = async (file: File) => {
    setError('');
    setUploading(true);
    try {
      const ct = file.type as 'image/png' | 'image/jpeg' | 'image/webp';
      const { url, s3Key } = await api.slotUploadUrl(matchId, slot.gameNum, slot.playerId, compId, ct);
      const putRes = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': ct } });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      await api.slotConfirm(matchId, slot.gameNum, slot.playerId, compId, s3Key);
      await queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const flagSummary = qualityFlags.length > 0
    ? qualityFlags.filter(f => f !== 'OK').join(', ')
    : '';

  // Display label: prefer the snapped roster gamertag, fall back to the
  // last 4 chars of the playerId. Truncate at 12 chars so a long gamertag
  // doesn't blow the chip out.
  const labelText = (displayName ?? slot.playerId.slice(-4)).slice(0, 12);

  return (
    <div className="group relative inline-flex flex-wrap items-center gap-y-0.5">
      <button
        type="button"
        onClick={canInspect && slot.status !== 'PENDING' ? () => setInspecting(true) : undefined}
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-1 font-mono text-[10px]',
          badge.bg, badge.text,
          excluded && 'line-through opacity-60',
          canInspect && slot.status !== 'PENDING' && 'cursor-pointer hover:brightness-125',
          (!canInspect || slot.status === 'PENDING') && 'cursor-default',
        )}
        title={[
          displayName ? `${displayName} (${slot.playerId})` : slot.playerId,
          badge.label,
          canInspect && slot.status !== 'PENDING' && 'click to inspect screenshot',
          slot.flaggedReason && slot.flaggedReason,
          flagSummary && `flags: ${flagSummary}`,
          excluded && 'EXCLUDED FROM CONSENSUS',
          missedDetail,
          notRosteredTag,
        ].filter(Boolean).join(' · ')}
      >
        {badge.icon}
        <span>{labelText}</span>
        {isMine && <span className="ml-0.5 text-forge-300">●</span>}
        {qualityFlags.includes('DISPUTED') && (
          <span className="ml-0.5 text-amber-300" title="Disputed">!</span>
        )}
        {qualityFlags.includes('STAGE2_REJECTED') && (
          <span className="ml-0.5 text-orange-300" title="Stage 2 rejected">⚠</span>
        )}
        {qualityFlags.includes('UNEXPECTED_NAME') && (
          <span className="ml-0.5 text-red-300" title="Unexpected name on scoreboard — not in either clan roster">☠</span>
        )}
        {qualityFlags.includes('MISSING_ROSTER_MEMBER') && (
          <span className="ml-0.5 text-amber-300" title="A roster member is missing from this scoreboard">?</span>
        )}
        {qualityFlags.includes('MODE_MISMATCH') && (
          <span className="ml-0.5 text-red-300" title="Wrong game mode — parsed scoreboard mode doesn't match this game's expected mode">≠</span>
        )}
        {qualityFlags.includes('STRIP_OWNER_MISMATCH') && (
          <span className="ml-0.5 text-pink-400" title="This screenshot's own uploader strip belongs to a DIFFERENT roster player than this slot — likely a mis-filed upload (or worse). Its K/D is not trusted as fact.">⇄</span>
        )}
        {qualityFlags.includes('ACTIVISION_ID_MISMATCH') && (
          <span className="ml-0.5 text-fuchsia-400" title="This screenshot's Activision ID (#number) doesn't match this player's REGISTERED Activision ID — an exact identity mismatch, stronger evidence than a name mismatch. Its K/D is not trusted as fact.">≢</span>
        )}
        {excluded && (
          <span className="ml-0.5 text-red-300" title="Admin excluded">⛔</span>
        )}
      </button>
      {/* Honest MISSED provenance — typography only, no colour strip (see
          no_severity_highlight_strips). basis-full forces this onto its own
          line inside the flex-wrap row above without disturbing the chip's
          own layout. */}
      {missedDetail && (
        <span className="basis-full font-mono text-[9px] leading-tight text-iron-500">{missedDetail}</span>
      )}
      {/* Roster-integrity flag — same restrained typography-only treatment as
          missedDetail above, no colour strip (no_severity_highlight_strips).
          Observational only; never implies the submission was blocked. */}
      {notRosteredTag && (
        <span className="basis-full font-mono text-[9px] uppercase tracking-wide leading-tight text-iron-500">{notRosteredTag}</span>
      )}
      {canUpload && (
        // Wrap mode — drag a screenshot straight onto the chip, same
        // handleUpload the click path already uses; no layout change.
        <ImageDropzone onFile={handleUpload} accept="image/png,image/jpeg,image/webp" disabled={uploading} className="ml-1 inline-block">
          <label
            className={cn(
              'inline-flex cursor-pointer items-center rounded px-1.5 py-1 font-mono text-[10px] text-white',
              isProxy ? 'bg-amber-700 hover:bg-amber-600' : 'bg-forge-600 hover:bg-forge-500',
            )}
            // Name the GAME and MODE on the control itself, not just in the
            // column header (F7/P3, filed 2026-07-20 and still open): in a
            // 24-cell grid the header scrolls out of sight, and every chip
            // looks identical. Seven mis-filed uploads on one match, by BOTH
            // clans independently — two teams making the same mistake is a UI
            // signal, not a discipline problem.
            title={`Game ${slot.gameNum} · ${MODE_LABEL(slot.gameMode)} — ${isProxy ? `upload on behalf of ${labelText}` : 'upload your screenshot'}, or drag one here`}
          >
            <Upload size={10} className="mr-0.5" />
            {uploading ? '…' : (isProxy ? 'Proxy' : 'Upload')}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={uploading}
              className="hidden"
              // RESET THE INPUT AFTER EVERY PICK (Lisa, 2026-07-23).
              //
              // Two bugs in one line. A file input RETAINS its selection, and
              // `change` only fires when the selection CHANGES — so:
              //   1. the element holds a File after upload, which is the only
              //      mechanism on our side that can put a previously-chosen
              //      image into a later upload without the user choosing it;
              //   2. re-picking the SAME file for a slot silently does
              //      NOTHING, because no change event fires — the upload just
              //      appears not to happen.
              //
              // Both matter here: on two separate matches the same image ended
              // up under multiple player/game slots. The request log proves the
              // SERVER routed every upload to the slot that was clicked (24/24,
              // in order), so whatever went wrong handed the browser the wrong
              // file at pick time. This closes the only path by which that can
              // happen without a human choosing it.
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = '';
              }}
            />
          </label>
        </ImageDropzone>
      )}
      {/* GOD-policy L7b, Piece 2 — quiet "nothing to submit" affordance.
          Visible on the slot holder's own PENDING slot, and to their
          captain/leader/admin on any own-side PENDING slot (canDeclare
          mirrors canUpload's exact scope). Inline confirm + optional reason
          — no browser prompt(). */}
      {canDeclare && !declaring && (
        <button
          type="button"
          onClick={() => setDeclaring(true)}
          className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-1 font-mono text-[10px] text-iron-400 opacity-0 transition-opacity hover:bg-iron-800 hover:text-iron-200 group-hover:opacity-100"
          title="Declare nothing to submit for this slot"
        >
          <Ban size={10} />
          Nothing to submit
        </button>
      )}
      {canDeclare && declaring && (
        <span className="ml-1 inline-flex items-center gap-1 rounded border border-iron-700 bg-iron-900 px-1.5 py-1">
          <input
            type="text"
            value={declareReason}
            onChange={e => setDeclareReason(e.target.value.slice(0, 200))}
            placeholder="Reason (optional)"
            maxLength={200}
            className="w-24 border-none bg-transparent font-mono text-[10px] text-iron-200 placeholder:text-iron-600 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => declareMutation.mutate(declareReason.trim())}
            disabled={declareMutation.isPending}
            className="rounded bg-iron-700 px-1 py-0.5 font-mono text-[10px] text-iron-100 hover:bg-iron-600"
            title="Confirm — nothing to submit"
          >
            {declareMutation.isPending ? '…' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={() => { setDeclaring(false); setDeclareReason(''); }}
            className="rounded px-1 py-0.5 font-mono text-[10px] text-iron-500 hover:text-iron-300"
            title="Cancel"
          >
            <X size={10} />
          </button>
        </span>
      )}
      {canFlag && slot.status !== 'PENDING' && (
        <button
          onClick={() => flagMutation.mutate({ flag: 'ADMIN_FLAGGED' as ShotQualityFlag, remove: excluded })}
          className={cn(
            'ml-1 inline-flex items-center rounded p-1 font-mono text-[10px] opacity-0 transition-opacity group-hover:opacity-100',
            excluded ? 'bg-iron-700 text-iron-200 hover:bg-iron-600' : 'bg-red-900/50 text-red-200 hover:bg-red-900',
            // Already-flagged or excluded slots stay visible — they're the
            // ones admins are most likely to click to restore.
            (excluded || qualityFlags.length > 0) && 'opacity-100',
          )}
          title={excluded ? 'Restore to consensus' : 'Mark problematic — exclude from consensus'}
          disabled={flagMutation.isPending}
          aria-label={excluded ? 'Restore slot to consensus' : 'Flag slot as problematic'}
        >
          <Flag size={10} />
        </button>
      )}
      {error && <div className="mt-1 font-mono text-[10px] text-red-400">{error}</div>}
      {inspecting && (
        <SlotInspectModal
          matchId={matchId}
          compId={compId}
          gameNum={slot.gameNum}
          playerId={slot.playerId}
          displayName={displayName ?? slot.playerId.slice(-4)}
          playerIdToDisplay={playerIdToDisplay}
          canEdit={canInspect}
          onClose={() => setInspecting(false)}
        />
      )}
    </div>
  );
}

// SlotInspectModal — admin/founder-only screenshot + per-shot OCR drawer.
// Loads /matchcard/.../image-url which returns presigned URLs to the
// original + processed S3 images plus the slot's parsedScoreboard +
// uploaderStats. Editing a value writes a cellOverride at the consensus
// level (highest-priority pool entry), not a mutation of the per-shot
// OCR row — that way one admin-corrected value outranks every OCR vote
// across all shots for that (game, player, field).
function SlotInspectModal({
  matchId, compId, gameNum, playerId, displayName, canEdit, onClose, playerIdToDisplay,
}: {
  matchId: string; compId: string; gameNum: number; playerId: string;
  displayName: string; canEdit: boolean;
  onClose: () => void;
  playerIdToDisplay?: Map<string, string>;
}) {
  // Readings rows carry the SNAPPED canonical player ids (ghost-… for ghost
  // seats). Show gamertags, never internal ids — same map the slot table uses.
  const nameFor = (id: string): string => {
    const hash = id.indexOf('#');
    if (hash > 0) {
      // uploader-strip form "name#discriminator" — map the name part only
      const mapped = playerIdToDisplay?.get(id.slice(0, hash));
      if (mapped) return `${mapped}#${id.slice(hash + 1)}`;
    }
    return playerIdToDisplay?.get(id) ?? id;
  };
  const [showProcessed, setShowProcessed] = useState(false);
  const [editing, setEditing] = useState<{ player: string; field: ScoreCellField; current?: number } | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['slotImage', matchId, gameNum, playerId],
    // compId is REQUIRED for an org-manager/comp-manager caller (founder/
    // admin bypasses it server-side) — see api.slotImageUrl's own
    // comment. compId is always in scope on this real match page.
    queryFn: () => api.slotImageUrl(matchId, gameNum, playerId, compId),
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const overrideMut = useMutation({
    mutationFn: ({ player, field, value }: { player: string; field: ScoreCellField; value: number }) =>
      api.cellOverride(matchId, gameNum, player, field, compId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
      setEditing(null);
    },
  });

  const parsed = data?.parsedScoreboard as { teams?: Array<{ rows: Array<Record<string, unknown>> }> } | null;
  const rows: Array<{ player: string; values: Record<string, unknown> }> = [];
  for (const team of parsed?.teams ?? []) {
    for (const row of team.rows ?? []) {
      const player = (row as { player?: string }).player ?? '';
      if (player) rows.push({ player, values: row });
    }
  }

  // Per-shot OCR TABLE readings only — objectiveKills/captures deliberately
  // excluded from this local FIELDS list: they never appear in a
  // parsedScoreboard TABLE row (only on the strip, see uploaderStats below),
  // so there's nothing for this per-shot table to show for them.
  const FIELDS: ScoreCellField[] = ['score', 'objScore', 'kills', 'deaths', 'time', 'plants', 'defuses', 'overloads'];
  // Exhaustive over the full ScoreCellField union (TS requires it) even
  // though objectiveKills/captures are never looked up via FIELDS above —
  // their row-key entries here are inert placeholders.
  const FIELD_TO_ROW_KEY: Record<ScoreCellField, string> = {
    score: 'score', objScore: 'objectiveScore', kills: 'eliminations', deaths: 'deaths',
    time: 'time', plants: 'plants', defuses: 'defuses', overloads: 'overloads',
    objectiveKills: 'objectiveKills', captures: 'captures',
  };
  const valueOf = (row: Record<string, unknown>, f: ScoreCellField): number | undefined => {
    const k = FIELD_TO_ROW_KEY[f];
    const top = row[k];
    if (typeof top === 'number') return top;
    if (typeof top === 'string') { const n = Number(top); if (!isNaN(n)) return n; }
    const raw = (row.rawValues as Record<string, unknown> | undefined)?.[k];
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') { const n = Number(raw); if (!isNaN(n)) return n; }
    return undefined;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl border border-forge-700/40 bg-iron-900 shadow-2xl"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-iron-800 px-6 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-iron-500">
              Game {gameNum} · slot inspect
            </p>
            <h2 className="mt-1 font-display text-xl font-black uppercase tracking-tight text-white">{displayName}</h2>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 border border-iron-700 hover:border-iron-500 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300"
          >
            <X size={12} strokeWidth={2.5} /> Close
          </button>
        </div>

        {isLoading && (
          <div className="px-6 py-8 text-center font-mono text-xs text-iron-500">Loading screenshot…</div>
        )}
        {error && (
          <div className="px-6 py-4 text-xs text-red-400">
            Couldn't load slot — {(error as Error).message ?? 'unknown error'}
          </div>
        )}

        {data && (
          <div className="grid gap-6 p-6 lg:grid-cols-2">
            {/* Screenshot column */}
            <div>
              <div className="mb-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-iron-500">
                <span>Screenshot</span>
                <button
                  onClick={() => setShowProcessed(s => !s)}
                  className="ml-auto border border-iron-700 px-2 py-0.5 hover:border-iron-500"
                >
                  {showProcessed ? 'show original' : 'show processed'}
                </button>
              </div>
              <a href={showProcessed ? data.processedUrl : data.originalUrl} target="_blank" rel="noreferrer">
                <img
                  src={showProcessed ? data.processedUrl : data.originalUrl}
                  alt={`${displayName} game ${gameNum}`}
                  className="w-full border border-iron-800 bg-iron-950"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </a>
              <p className="mt-2 text-[10px] text-iron-500">
                Click to open full-size in a new tab.
                {data.ocrConfidence != null && (
                  <> · OCR confidence: {Math.round(data.ocrConfidence * 100)}%</>
                )}
                {data.ocrTier && <> · tier {data.ocrTier}</>}
              </p>
            </div>

            {/* OCR table column */}
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-500">
                Per-shot OCR readings
                {canEdit && <span className="ml-2 text-forge-400">click any cell to override</span>}
              </p>
              {rows.length === 0 ? (
                <p className="font-mono text-xs text-iron-500">No parsed rows on this slot.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-[11px]">
                    <thead className="text-iron-500">
                      <tr>
                        <th className="px-2 py-1 text-left font-display text-[9px] uppercase tracking-wider">Player</th>
                        {FIELDS.map(f => (
                          <th key={f} className="px-2 py-1 text-right font-display text-[9px] uppercase tracking-wider">{FIELD_LABEL[f]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ player, values }) => (
                        <tr key={player} className="border-t border-iron-800">
                          <td className="whitespace-nowrap px-2 py-1.5 text-iron-200">{nameFor(player)}</td>
                          {FIELDS.map(f => {
                            const v = valueOf(values, f);
                            const empty = v === undefined;
                            return (
                              <td
                                key={f}
                                className={cn(
                                  'px-2 py-1.5 text-right tabular-nums',
                                  empty ? 'text-iron-700' : 'text-iron-100',
                                  canEdit && 'cursor-pointer hover:bg-iron-900',
                                )}
                                onClick={() => canEdit && setEditing({ player, field: f, current: v })}
                              >
                                {empty ? '—' : v}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Uploader bottom-strip stats */}
              {data.uploaderStats && (
                <div className="mt-4 border border-iron-800 bg-iron-950 p-3 text-xs">
                  <p className="mb-2 font-mono text-[9px] uppercase tracking-wider text-iron-500">Uploader bottom-strip</p>
                  <p className="font-mono text-iron-300">
                    {data.uploaderStats.gamertag ? nameFor(data.uploaderStats.gamertag) : '—'}
                    {data.uploaderStats.eliminations != null && <> · K {data.uploaderStats.eliminations}</>}
                    {data.uploaderStats.deaths != null && <> · D {data.uploaderStats.deaths}</>}
                    {data.uploaderStats.objectiveScore != null && <> · Obj {data.uploaderStats.objectiveScore}</>}
                  </p>
                  {/* 2026-07-14 (Lisa — "get as many stats as possible off the
                      screenshot") — everything the strip parser captured but
                      the primary K/D/Obj line above doesn't show (Objective
                      Kills, Captures, Time, or any other mode-specific
                      trailing stat) — see scoreboardAnalysis.ts's
                      parseTrailingStatLine / selectedPlayer.extras. Read-only,
                      same plain border+type styling as the rest of this
                      modal — no readable stat stays hidden. */}
                  {data.uploaderStats.extras && data.uploaderStats.extras.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-iron-800 pt-2 font-mono text-iron-400">
                      {data.uploaderStats.extras.map((extra, i) => (
                        <span key={`${extra.label}-${i}`}>
                          {extra.label} <span className="text-iron-200">{extra.value}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {data.qualityFlags && data.qualityFlags.length > 0 && (
                <p className="mt-3 text-[10px] text-amber-300">
                  Quality flags: {data.qualityFlags.join(', ')}
                </p>
              )}
              {data.flaggedReason && (
                <p className="mt-1 text-[10px] text-amber-300">Reason: {data.flaggedReason}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <SlotInspectCellEditor
          player={editing.player}
          playerLabel={nameFor(editing.player)}
          field={editing.field}
          current={editing.current}
          isSaving={overrideMut.isPending}
          onSave={(value) => overrideMut.mutate({ player: editing.player, field: editing.field, value })}
          onClose={() => setEditing(null)}
        />
      )}
    </div>,
    document.body,
  );
}

// Small inline editor used by SlotInspectModal — owns its own draft state.
// Saves via the parent's cellOverride mutation (consensus-level priority).
function SlotInspectCellEditor({
  player, playerLabel, field, current, isSaving, onSave, onClose,
}: {
  // `player` is the canonical id (the override endpoint key); `playerLabel`
  // is the gamertag shown to the human — never show the id.
  player: string;
  playerLabel?: string;
  field: ScoreCellField;
  current: number | undefined;
  isSaving: boolean;
  onSave: (value: number) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<string>(current != null ? String(current) : '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Enter')  {
        e.preventDefault();
        const n = parseInt(draft, 10);
        if (Number.isFinite(n)) onSave(n);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [draft, onSave, onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-forge-700/40 bg-iron-900 p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-iron-500">Override consensus</p>
        <h3 className="mt-1 font-display text-lg font-black uppercase tracking-tight text-white">
          {playerLabel ?? player} <span className="text-iron-500">·</span> <span className="text-forge-300">{FIELD_LABEL[field]}</span>
        </h3>
        <p className="mt-2 text-xs text-iron-400">
          Current consensus: <span className="font-mono tabular-nums text-iron-200">{current ?? '—'}</span>.
          Saving outranks every OCR vote across all shots for this cell.
        </p>
        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          className="mt-4 w-full border-2 border-forge-500 bg-iron-950 px-4 py-3 text-right font-mono text-2xl tabular-nums text-white focus:border-forge-400 focus:outline-none"
          placeholder={current != null ? String(current) : '0'}
        />
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="inline-flex items-center gap-2 border border-iron-700 hover:border-iron-500 px-4 py-2 font-display text-xs font-black uppercase tracking-wider text-iron-200"
          >
            <X size={12} strokeWidth={2.5} /> Cancel
          </button>
          <button
            onClick={() => { const n = parseInt(draft, 10); if (Number.isFinite(n)) onSave(n); }}
            disabled={isSaving || !draft}
            className="inline-flex items-center gap-2 bg-forge-600 hover:bg-forge-500 px-4 py-2 font-display text-xs font-black uppercase tracking-wider text-white shadow-ember disabled:opacity-50"
          >
            <Save size={12} strokeWidth={2.5} />
            {isSaving ? 'Saving…' : 'Save override'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OcrDigestionPanel — Wave 6. Brings the OCR Lab's "run digestion + watch it
// happen live" experience to the real match page, gated on canOperateDigestion
// (see CardBody) rather than isAdmin/isLeader.
//
// Live-in-flight signal: unlike the Lab (single operator, so a local
// batchInFlight boolean set on click is a fine proxy), a real match page can
// have multiple admins/owners watching, or a viewer reloading mid-run. So the
// PRIMARY signal here is the polled `status === 'OCR'` from the match card
// itself (already being polled by cardQuery in MatchCardLive) — a fresh page
// load mid-digestion correctly shows the live panel. `justTriggered` is only
// a same-tab, same-click optimistic nudge for the gap between clicking Run
// and the next cardQuery poll landing; it clears itself once the server
// confirms (status flips to OCR) or the dispatch itself fails.
// ─────────────────────────────────────────────────────────────────────────────

function OcrDigestionPanel({ data, matchId, compId, status, clan1, clan2 }: {
  data: MatchCardV2Response;
  matchId: string;
  compId: string;
  status: MatchCardStatus;
  clan1: string;
  clan2: string;
}) {
  const queryClient = useQueryClient();
  const [justTriggered, setJustTriggered] = useState(false);
  const inFlight = status === 'OCR' || justTriggered;

  useEffect(() => {
    // The local nudge only matters while we're waiting for status to leave
    // CLOSED and become OCR — once it does (or moves anywhere else), the
    // server has either confirmed the dispatch or made it moot.
    if (status !== 'CLOSED') setJustTriggered(false);
  }, [status]);

  const runMutation = useMutation({
    mutationFn: () => api.runOcrBatch(matchId, compId),
    onMutate: () => setJustTriggered(true),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
    onError: () => setJustTriggered(false),
  });

  // Live activity feed — only polls while a batch is in flight. Mirrors the
  // OCR Lab's own feedQuery, but always passes compId (see api.ts's
  // matchCardOcrFeed — required for org-manager callers, optional for
  // founders) and keys off the server-driven `inFlight` above rather than a
  // local-only proxy.
  const [feedSince, setFeedSince] = useState<string | undefined>(undefined);
  const [feedEvents, setFeedEvents] = useState<OcrFeedEvent[]>([]);
  const feedQuery = useQuery({
    queryKey: ['ocrfeed', matchId, compId, feedSince],
    queryFn: () => api.matchCardOcrFeed(matchId, feedSince, compId),
    enabled: inFlight,
    refetchInterval: inFlight ? 1500 : false,
  });
  useEffect(() => {
    if (!feedQuery.data?.events?.length) return;
    setFeedEvents(prev => [...prev, ...feedQuery.data!.events].slice(-200));
    if (feedQuery.data.latestSk) setFeedSince(feedQuery.data.latestSk);
  }, [feedQuery.data]);
  // Reset the feed each time a run starts — covers both "I just clicked Run"
  // and "I loaded this page and a run is already underway" (feedSince starts
  // undefined either way, so the first poll does the full begins_with scan).
  useEffect(() => {
    if (status === 'OCR') { setFeedEvents([]); setFeedSince(undefined); }
  }, [status === 'OCR']);

  // Shot-flag toggle from the per-shot drawer. NOTE: this hits the SAME
  // /slot/:g/:p/flag endpoint the OCR Lab uses, which is gated server-side
  // by isAdmin/isLeader/isFounder (handleShotFlag) — a DIFFERENT boundary
  // than canOperateDigestion that gates this whole panel. An org-manager who
  // isn't also a Discord admin/leader will get a clean 403 here rather than
  // a silently-broken button; the server remains the sole enforcer, same
  // principle as the run-digestion button's client-side status window above.
  const [flagError, setFlagError] = useState('');
  const flagMutation = useMutation({
    mutationFn: (vars: { gameNum: number; playerId: string; remove: boolean }) =>
      api.shotFlag(matchId, vars.gameNum, vars.playerId, compId, 'ADMIN_FLAGGED' as ShotQualityFlag, { remove: vars.remove }),
    onSuccess: () => { setFlagError(''); queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }); },
    onError: (err) => setFlagError(parseApiError(err).message),
  });

  return (
    <section className="rounded-xl border border-blue-700/40 bg-iron-900 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
          <Cpu size={16} className="text-blue-300" /> OCR Digestion
        </h3>
        {status === 'OCR' ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-600/60 bg-blue-900/30 px-3 py-1 font-mono text-xs uppercase tracking-wider text-blue-300">
            <Flame size={12} className="animate-pulse" /> processing
          </span>
        ) : (
          <button
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending || justTriggered}
            className="rounded-md bg-blue-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {runMutation.isPending || justTriggered ? 'Dispatching…' : 'Run digestion'}
          </button>
        )}
      </div>

      {runMutation.isError && (
        <p className="mb-2 font-mono text-xs text-red-300">✗ {(runMutation.error as Error).message}</p>
      )}
      {flagError && (
        <p className="mb-2 font-mono text-xs text-red-300">✗ flag failed: {flagError}</p>
      )}

      {inFlight ? (
        <div className="space-y-3">
          <ShotInspectGrid
            data={data}
            spawned={{ matchId, compId, clan1, clan2 }}
            events={feedEvents}
            onFlag={(slot) => {
              const excluded = (slot as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus ?? false;
              flagMutation.mutate({ gameNum: slot.gameNum, playerId: slot.playerId, remove: excluded });
            }}
          />
          <OcrActivityFeedPanel events={feedEvents} batchInFlight={inFlight} />
        </div>
      ) : (
        <p className="font-mono text-xs text-iron-400">
          Kicks the same v2 OCR pipeline as the founder OCR Lab against every uploaded slot on this match. Server enforces the
          founder/org-owner/org-admin gate independently — this button is just a shortcut for it.
        </p>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ScoreboardConsensus — per-cell consensus computed from slots[], with
// click-to-edit cells when the viewer is admin/leader. Members see read-only.
// ─────────────────────────────────────────────────────────────────────────────

const FIELD_LABEL: Record<ScoreCellField, string> = {
  score: 'Score',
  objScore: 'Obj',
  kills: 'K',
  deaths: 'D',
  time: 'Time',
  plants: 'Plants',
  defuses: 'Defuses',
  overloads: 'OL',
  // 2026-07-14 (Lisa — Hardpoint objective columns): Hardpoint's own
  // personal-strip equivalent of SND's Plants/Defuses and Overload's OL —
  // see applyUploaderStripPrecedence in lib/consensus.ts for how these two
  // get sourced (strip-only, identity-guarded, never table-voted).
  objectiveKills: 'Obj Kills',
  captures: 'Captures',
};

// Strip-only fields with no backend admin-override/flag support yet (not in
// matchCardV2.ts's VALID_FIELDS/VALID_FLAG_FIELDS — see the ScoreCellField
// doc comment in lib/api.ts). Rendered read-only here rather than wiring a
// click-to-edit/flag affordance that would 400 against the backend today.
const STRIP_ONLY_READONLY_FIELDS = new Set<ScoreCellField>(['objectiveKills', 'captures']);

// Small CellFlag-shaped badges for cells backed by server-canonical
// consensus (see mergeServerConsensus). AGREED — the default, unremarkable
// case — is deliberately dropped: showing it on every single agreed cell
// would just be noise; the point of a badge is to flag something worth a
// second look (a correction, a dispute, a gap).
const FLAG_BADGE_STYLE: Record<string, string> = {
  MAJORITY: 'bg-iron-700 text-iron-200',
  TIE: 'bg-amber-900/40 text-amber-300',
  ALL_DIFFERENT: 'bg-red-900/40 text-red-300',
  STAGE2_CORRECTED: 'bg-blue-900/40 text-blue-300',
  NO_READINGS: 'bg-iron-800 text-iron-500',
  // Lisa, 2026-07-13 — K/D strip precedence provenance badges. STRIP marks a
  // cell filled/overridden from the player's own uploaded bottom-strip
  // (uploaderStats), never a cross-shot table vote — see
  // applyUploaderStripPrecedence in lib/consensus.ts. STRIP_TABLE_MISMATCH
  // additionally fires when the table consensus disagreed with the strip —
  // the strip still wins the cell, this is purely a "look closer" flag.
  STRIP: 'bg-emerald-900/40 text-emerald-300',
  STRIP_TABLE_MISMATCH: 'bg-red-900/40 text-red-300',
};

function ConsensusFlagBadges({ flags }: { flags?: string[] }) {
  const shown = (flags ?? []).filter(f => f !== 'AGREED');
  if (shown.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-0.5">
      {shown.map(f => (
        <span key={f} className={cn('rounded px-1 py-px font-mono text-[7px] uppercase tracking-wide', FLAG_BADGE_STYLE[f] ?? 'bg-iron-800 text-iron-400')}>
          {f.replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}

function ScoreboardConsensus({ slotsByGame, gameModes, matchId, compId, clan1Id, clan2Id, clan1Label, clan2Label, clan1Roster, clan2Roster, clan1Snap, clan2Snap, canEdit, canFlag, isAdmin, serverConsensus }: {
  slotsByGame: Record<number, SubmissionSlotRow[]>;
  gameModes: string[];
  matchId: string;
  compId: string;
  clan1Id: string;
  clan2Id: string;
  clan1Label?: string;
  clan2Label?: string;
  clan1Roster: string[];
  clan2Roster: string[];
  // Slot-identity arrays (Discord ids / ghost ids), SAME order as
  // clan1Roster/clan2Roster — lets aggregateConsensus/applyUploaderStripPrecedence
  // attribute a slot's uploaderStats by WHO uploaded it rather than relying
  // solely on fuzzy-matching the strip's own (sometimes OCR-mangled)
  // gamertag reading. Without these, K/D strip attribution silently degrades
  // to gamertag-fuzzy-match-only — see playerIdToDisplay below.
  clan1Snap?: string[];
  clan2Snap?: string[];
  canEdit: boolean;
  canFlag: boolean;
  isAdmin: boolean;
  // Server-canonical per-cell consensus from the match's own
  // ocrBatchSummary (set once a digestion batch has completed) — preferred,
  // per-cell, over the client recompute below when present. See
  // mergeServerConsensus in lib/consensus.ts.
  serverConsensus?: ServerCellConsensusRow[];
}) {
  const allSlots = Object.values(slotsByGame).flat();
  const roster = useMemo(() => [...clan1Roster, ...clan2Roster], [clan1Roster, clan2Roster]);
  // Same id->display map SlotGrid builds for the slot table above — kept
  // local here (rather than threaded down from the page) since this is the
  // only other consumer that needs it, and it's cheap to derive from the
  // same two roster-snapshot arrays already passed in for the slot grid.
  const playerIdToDisplay = useMemo(() => {
    const m = new Map<string, string>();
    const c1Snap = clan1Snap ?? []; const c2Snap = clan2Snap ?? [];
    for (let i = 0; i < c1Snap.length; i++) {
      const id = c1Snap[i]; const name = clan1Roster[i];
      if (id && name) m.set(id, name);
    }
    for (let i = 0; i < c2Snap.length; i++) {
      const id = c2Snap[i]; const name = clan2Roster[i];
      if (id && name) m.set(id, name);
    }
    return m;
  }, [clan1Snap, clan2Snap, clan1Roster, clan2Roster]);
  const { perGame: clientPerGame, players: clientPlayers } = useMemo(
    () => aggregateConsensus(allSlots, roster, {}, playerIdToDisplay),
    [allSlots, roster, playerIdToDisplay],
  );
  const { perGame: tablePerGame, players: tablePlayers } = useMemo(
    () => mergeServerConsensus(clientPerGame, clientPlayers, serverConsensus, roster),
    [clientPerGame, clientPlayers, serverConsensus, roster],
  );
  // K/D strip precedence (Lisa, 2026-07-13) — deliberately the LAST step:
  // must run AFTER mergeServerConsensus, or a plain table-vote server cell
  // would clobber the strip resolution the moment digestion has run. See
  // applyUploaderStripPrecedence's own doc comment in lib/consensus.ts.
  const { perGame, players } = useMemo(
    () => applyUploaderStripPrecedence(tablePerGame, tablePlayers, allSlots, roster, {}, playerIdToDisplay),
    [tablePerGame, tablePlayers, allSlots, roster, playerIdToDisplay],
  );
  const games = Object.keys(perGame).map(Number).sort();
  if (games.length === 0) return null;

  // Determine which fields are populated for each game (mode-dependent).
  const fieldsPerGame: Record<number, ScoreCellField[]> = {};
  for (const g of games) {
    const fields = new Set<string>();
    for (const agg of perGame[g] ?? []) fields.add(agg.field);
    fieldsPerGame[g] = (['score', 'objScore', 'kills', 'deaths', 'time', 'plants', 'defuses', 'overloads', 'objectiveKills', 'captures'] as ScoreCellField[]).filter(f => fields.has(f));
  }

  const clan1Set = new Set(clan1Roster);
  const clan2Set = new Set(clan2Roster);
  const clan1Name = clanLabelFrom(clan1Label, clan1Id);
  const clan2Name = clanLabelFrom(clan2Label, clan2Id);

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
          <Trophy size={16} /> Scoreboard Consensus
          {canEdit && (
            <span className="rounded bg-forge-900/40 px-2 py-0.5 font-mono text-[11px] text-forge-300">{isAdmin ? 'admin edit' : ''}</span>
          )}
        </h3>
        <p className="font-mono text-[11px] text-iron-500">
          {canEdit ? 'click a cell to override — overrides outrank OCR' : canFlag ? 'spotted something wrong? raise a flag — admins review' : 'view-only'}
        </p>
      </div>

      <div className="space-y-4">
        {games.map(g => {
          const cells = (perGame[g] ?? []) as CellAggregate[];
          const playersInG = players[g] ?? [];
          const fields = fieldsPerGame[g] ?? [];
          const c1Players = playersInG.filter(p => clan1Set.has(p));
          const c2Players = playersInG.filter(p => clan2Set.has(p));
          const orphans = playersInG.filter(p => !clan1Set.has(p) && !clan2Set.has(p));
          const team1 = orphans.length ? [...c1Players, ...orphans] : c1Players;

          const teamTotal = (ps: string[]) =>
            ps.reduce((sum, p) => sum + (cells.find(c => c.player === p && c.field === 'score')?.consensus ?? 0), 0);
          const t1Total = teamTotal(team1);
          const t2Total = teamTotal(c2Players);
          const decided = t1Total !== t2Total && (t1Total > 0 || t2Total > 0);
          const board1 = { name: clan1Name, players: team1, total: t1Total };
          const board2 = { name: clan2Name, players: c2Players, total: t2Total };
          const [top, bottom] = t2Total > t1Total ? [board2, board1] : [board1, board2];

          return (
            <div key={g} className="rounded-lg border border-iron-700 bg-iron-950 p-3">
              <div className="mb-2 flex items-baseline gap-3">
                <div className="font-display text-sm font-bold uppercase tracking-wider text-iron-100">Game {g}</div>
                {gameModes[g - 1] && (
                  <span className="rounded border border-forge-700/50 bg-forge-900/30 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-forge-300">
                    {gameModes[g - 1]}
                  </span>
                )}
                <div className="font-mono text-[11px] text-iron-500">
                  {decided ? `${top.name} ${top.total} – ${bottom.total} ${bottom.name}` : `${board1.name} ${t1Total} – ${t2Total} ${board2.name} (undecided)`}
                </div>
              </div>
              <ConsensusGameTable
                gameNum={g}
                mode={gameModes[g - 1] ?? `Game ${g}`}
                players={top.players}
                cells={cells}
                fields={fields}
                matchId={matchId}
                compId={compId}
                canEdit={canEdit}
                canFlag={canFlag}
                teamLabel={top.name}
                isWinner={decided}
                hideHeader
              />
              <ConsensusGameTable
                gameNum={g}
                mode={gameModes[g - 1] ?? `Game ${g}`}
                players={bottom.players}
                cells={cells}
                fields={fields}
                matchId={matchId}
                compId={compId}
                canEdit={canEdit}
                canFlag={canFlag}
                teamLabel={bottom.name}
                isWinner={false}
                hideHeader
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ConsensusGameTable({ gameNum, mode, players, cells, fields, matchId, compId, canEdit, canFlag, teamLabel, isWinner, hideHeader }: {
  gameNum: number;
  mode: string;
  players: string[];
  cells: CellAggregate[];
  fields: ScoreCellField[];
  matchId: string;
  compId: string;
  canEdit: boolean;
  canFlag: boolean;
  teamLabel?: string;
  isWinner?: boolean;
  hideHeader?: boolean;
}) {
  if (players.length === 0) return null;
  return (
    <div className={cn('mt-2 overflow-hidden rounded-md border', isWinner ? 'border-amber-700/50 bg-amber-950/10' : 'border-iron-800 bg-iron-950')}>
      {!hideHeader && (
        <div className="flex items-center gap-2 border-b border-iron-800 bg-iron-950 px-3 py-1.5 font-mono text-[11px] uppercase">
          <span className="font-bold text-iron-200">Game {gameNum}</span>
          <span className="text-iron-500">·</span>
          <span className="text-forge-300">{mode}</span>
        </div>
      )}
      {teamLabel && (
        <div className={cn('flex items-center gap-2 px-3 py-1.5 font-mono text-[11px] uppercase', isWinner ? 'border-b border-amber-800/40 bg-amber-900/20 text-amber-200' : 'border-b border-iron-800 bg-iron-950 text-iron-200')}>
          {isWinner && <Trophy size={11} className="text-amber-300" />}
          <span className="font-bold">{teamLabel}</span>
          {isWinner && <span className="text-amber-300/70">winner</span>}
        </div>
      )}
      {/* Desktop / tablet — table layout */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-left font-mono text-xs" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '36%' }} />
            {fields.map(f => (
              <col key={f} style={{ width: `${64 / Math.max(1, fields.length)}%` }} />
            ))}
          </colgroup>
          <thead className="text-iron-500">
            <tr>
              <th className="px-3 py-1.5 font-display uppercase tracking-wider text-[10px]">Player</th>
              {fields.map(f => (
                <th key={f} className="px-3 py-1.5 text-right font-display uppercase tracking-wider text-[10px]">{FIELD_LABEL[f]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map(p => (
              <tr key={p} className="border-t border-iron-800/70">
                <td className="truncate whitespace-nowrap px-3 py-2 text-iron-200">{p}</td>
                {fields.map(f => {
                  const agg = cells.find(c => c.player === p && c.field === f);
                  // Strip-only fields (objectiveKills/captures) have no
                  // backend override/flag support yet — render read-only
                  // rather than a click-to-edit/flag affordance that 400s.
                  const readOnly = STRIP_ONLY_READONLY_FIELDS.has(f);
                  return (
                    <ConsensusCell
                      key={f}
                      cell={agg}
                      gameNum={gameNum}
                      player={p}
                      field={f}
                      matchId={matchId}
                      compId={compId}
                      canEdit={readOnly ? false : canEdit}
                      canFlag={readOnly ? false : canFlag}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — per-player card stack. Field/value chips in a 3-col grid;
          tap a chip to edit (admin) or flag (member) the cell. */}
      <div className="space-y-2 p-2 sm:hidden">
        {players.map(p => (
          <div key={p} className="border border-iron-800 bg-iron-950 p-3">
            <p className="mb-2 font-display text-sm font-bold uppercase tracking-wide text-iron-100">{p}</p>
            <div className="grid grid-cols-3 gap-1.5">
              {fields.map(f => {
                const agg = cells.find(c => c.player === p && c.field === f);
                // Same read-only gate as the desktop table above.
                const readOnly = STRIP_ONLY_READONLY_FIELDS.has(f);
                return (
                  <ConsensusChip
                    key={f}
                    cell={agg}
                    gameNum={gameNum}
                    player={p}
                    field={f}
                    matchId={matchId}
                    compId={compId}
                    canEdit={readOnly ? false : canEdit}
                    canFlag={readOnly ? false : canFlag}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Mobile-only single cell as a stacked chip: field label + value + agreement
// subline. Click handler matches the desktop ConsensusCell — opens the edit
// modal anchored to the chip when canEdit, raises a flag-popover when
// canFlag-only.
function ConsensusChip({ cell, gameNum, player, field, matchId, compId, canEdit, canFlag }: {
  cell?: CellAggregate;
  gameNum: number; player: string; field: ScoreCellField;
  matchId: string; compId: string; canEdit: boolean; canFlag: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [anchorTop, setAnchorTop] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: (newValue: number) => api.cellOverride(matchId, gameNum, player, field, compId, newValue),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
  });

  const value = cell?.consensus;
  const agreement = cell?.agreement ?? 0;
  const reads = cell?.reads ?? 0;
  const empty = value === undefined;

  const colour = empty ? 'text-iron-500'
    : agreement === 1 ? 'text-emerald-300'
    : agreement >= 0.67 ? 'text-iron-100'
    : agreement >= 0.5 ? 'text-amber-300'
    : 'text-red-300';

  const showSubline = agreement > 0 && agreement < 1 && reads > 1;

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 border border-iron-800 bg-iron-950 px-2 py-1.5 leading-tight',
        canEdit && 'cursor-pointer active:bg-iron-800',
      )}
      onClick={(e) => {
        if (!canEdit) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setAnchorTop(rect.top + rect.height / 2);
        setDraft(empty ? '' : String(value));
        setEditing(true);
      }}
    >
      <span className="font-mono text-[9px] font-bold uppercase tracking-wider text-iron-500">{FIELD_LABEL[field]}</span>
      <div className="flex items-baseline justify-between gap-1">
        <span className={cn('font-mono text-base tabular-nums', colour)}>{empty ? '—' : value}</span>
        {canFlag && !canEdit && !empty && (
          <ScoreFlagButton
            matchId={matchId}
            compId={compId}
            gameNum={gameNum}
            playerKey={player}
            field={field}
          />
        )}
      </div>
      {showSubline && (
        <span className="whitespace-nowrap text-[8px] text-iron-500">{Math.round(agreement * 100)}% · {reads}r</span>
      )}
      <ConsensusFlagBadges flags={cell?.flags} />
      {editing && (
        <CellEditModal
          player={player}
          field={field}
          currentValue={value}
          agreement={agreement}
          reads={reads}
          draft={draft}
          setDraft={setDraft}
          isSaving={mutation.isPending}
          anchorTop={anchorTop}
          onSave={() => { const n = parseInt(draft, 10); if (Number.isFinite(n)) mutation.mutate(n); }}
          onClose={() => { setEditing(false); setDraft(''); }}
        />
      )}
    </div>
  );
}

function ConsensusCell({ cell, gameNum, player, field, matchId, compId, canEdit, canFlag }: {
  cell?: CellAggregate;
  gameNum: number; player: string; field: ScoreCellField;
  matchId: string; compId: string; canEdit: boolean; canFlag: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Anchor the modal vertically near the row that triggered it, so editing
  // a cell at the top or bottom of a long page doesn't dump the modal off-
  // screen. Captured at click time from the <td>'s bounding rect.
  const [anchorTop, setAnchorTop] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: (newValue: number) => api.cellOverride(matchId, gameNum, player, field, compId, newValue),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
  });

  const value = cell?.consensus;
  const agreement = cell?.agreement ?? 0;
  const reads = cell?.reads ?? 0;

  const colour = value === undefined ? 'text-iron-700'
    : agreement === 1 ? 'text-emerald-300'
    : agreement >= 0.67 ? 'text-iron-100'
    : agreement >= 0.5 ? 'text-amber-300'
    : 'text-red-300';

  const showSubline = agreement > 0 && agreement < 1 && reads > 1;
  // Admins can click any cell — including empty ones — to fill it in. The
  // "blank spot" pattern means OCR didn't recover that field at all (e.g.
  // the player's row got dropped from one shot, or the field doesn't apply
  // to the game mode but the table still shows it). Click-to-fill lets
  // admins manually backfill from the screenshot.
  const empty = value === undefined;
  return (
    <td
      className={cn(
        'group px-3 py-2 text-right tabular-nums leading-tight',
        colour,
        empty && canEdit && 'text-iron-500 hover:text-iron-300',
        canEdit && 'cursor-pointer hover:bg-iron-900',
      )}
      title={
        cell
          ? cell.source === 'strip'
            ? `From the player's own uploaded screenshot (bottom-strip K/D)${cell.flags?.includes('STRIP_TABLE_MISMATCH') ? ' · disagrees with the table vote — strip wins' : ''}`
            : `${reads} reading${reads === 1 ? '' : 's'}, ${(agreement * 100).toFixed(0)}% agreement${cell.source === 'server' ? ' · server-verified (OCR digestion)' : ''}`
          : canEdit ? 'No OCR reading — click to fill' : 'no reading'
      }
      onClick={(e) => {
        if (!canEdit) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setAnchorTop(rect.top + rect.height / 2);
        setDraft(empty ? '' : String(value));
        setEditing(true);
      }}
    >
      <div className="flex items-center justify-end gap-1">
        <span className="text-sm">{empty ? '—' : value}</span>
        {canEdit && (
          <Pencil size={9} className="shrink-0 opacity-0 transition-opacity group-hover:opacity-50" />
        )}
        {canFlag && !canEdit && !empty && (
          <ScoreFlagButton
            matchId={matchId}
            compId={compId}
            gameNum={gameNum}
            playerKey={player}
            field={field}
          />
        )}
      </div>
      {showSubline && (
        <div className="mt-0.5 whitespace-nowrap text-[9px] text-iron-500">{Math.round(agreement * 100)}% · {reads}r</div>
      )}
      {cell?.flags?.some(f => f !== 'AGREED') && (
        <div className="mt-0.5 flex justify-end">
          <ConsensusFlagBadges flags={cell.flags} />
        </div>
      )}
      {editing && (
        <CellEditModal
          player={player}
          field={field}
          currentValue={value}
          agreement={agreement}
          reads={reads}
          draft={draft}
          setDraft={setDraft}
          isSaving={mutation.isPending}
          anchorTop={anchorTop}
          onSave={() => { const n = parseInt(draft, 10); if (Number.isFinite(n)) mutation.mutate(n); }}
          onClose={() => { setEditing(false); setDraft(''); }}
        />
      )}
    </td>
  );
}

// CellEditModal — viewport-centered overlay for editing a scoreboard cell.
// Always centers regardless of where the trigger cell sits — simpler and
// more predictable than anchoring to the click. Body scroll position is
// preserved automatically because the modal is `position: fixed` and the
// input auto-focuses with `preventScroll`. Esc cancels, Enter saves,
// backdrop click cancels.
function CellEditModal({
  player, field, currentValue, agreement, reads,
  draft, setDraft, isSaving, onSave, onClose,
}: {
  player: string;
  field: ScoreCellField;
  currentValue: number | undefined;
  agreement: number;
  reads: number;
  draft: string;
  setDraft: (v: string) => void;
  isSaving: boolean;
  anchorTop?: number | null;  // ignored — kept for API compat
  onSave: () => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Enter')  { e.preventDefault(); if (draft) onSave(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [draft, onClose, onSave]);

  // Focus the input WITHOUT scrolling the page — autoFocus prop calls
  // .focus() with default options which can scroll the underlying page
  // when the modal opens at the bottom of a long match card.
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  const empty = currentValue === undefined;

  // Portal to <body> — bypasses the `transform` on <main> (animate-forge-in)
  // which would otherwise create a containing block and trap `position:
  // fixed` to <main> instead of the viewport. With the portal, `fixed
  // inset-0` truly fixes to the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
       <div className="rounded-xl border border-forge-700/40 bg-iron-900 p-6 shadow-2xl">
        <div className="mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-iron-500">Edit cell</p>
          <h2 className="mt-1 font-display text-xl font-black uppercase tracking-tight text-white">
            {player} <span className="text-iron-500">·</span> <span className="text-forge-300">{FIELD_LABEL[field]}</span>
          </h2>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 text-xs">
          <div className="border border-iron-800 bg-iron-950 px-3 py-2">
            <p className="font-mono text-[9px] uppercase tracking-wider text-iron-500">Current</p>
            <p className="mt-1 font-mono text-lg tabular-nums text-iron-100">
              {empty ? <span className="text-iron-500">—</span> : currentValue}
            </p>
          </div>
          <div className="border border-iron-800 bg-iron-950 px-3 py-2">
            <p className="font-mono text-[9px] uppercase tracking-wider text-iron-500">OCR confidence</p>
            <p className="mt-1 font-mono text-lg tabular-nums text-iron-100">
              {reads === 0 ? <span className="text-iron-500">no reads</span> : `${Math.round(agreement * 100)}% · ${reads}r`}
            </p>
          </div>
        </div>

        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-iron-500">New value</span>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="mt-2 w-full border-2 border-forge-500 bg-iron-950 px-4 py-3 text-right font-mono text-2xl tabular-nums text-white focus:border-forge-400 focus:outline-none"
            placeholder={empty ? '0' : String(currentValue)}
          />
        </label>
        <p className="mt-2 text-[10px] text-iron-500">
          Saves as a {empty ? 'new' : ''} {FIELD_LABEL[field].toLowerCase()} value for {player}. This outranks OCR; future re-OCR won't overwrite it.
        </p>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 border border-iron-700 bg-iron-900 px-5 py-2.5 font-display text-xs font-black uppercase tracking-wider text-iron-200 hover:border-iron-500 hover:bg-iron-800 disabled:opacity-50"
          >
            <X size={14} strokeWidth={2.5} />
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving || !draft}
            className="inline-flex items-center justify-center gap-2 bg-forge-600 px-5 py-2.5 font-display text-xs font-black uppercase tracking-wider text-white shadow-ember hover:bg-forge-500 disabled:opacity-50"
          >
            <Save size={14} strokeWidth={2.5} />
            {isSaving ? 'Saving…' : 'Save override'}
          </button>
        </div>
       </div>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Arc 3 (scoring unification) — the confirm affordance the shipped UI never
// actually wired up (2026-07-05 OCR trust audit finding). Calls
// POST /matchcard/:matchId/confirm-scores, which derives games[]/nightWinner
// from the OCR'd slots and writes status CONFIRMED — the SAME shape manual
// result entry uses — so this match finally reaches computeStandings/R2/R4.
// ─────────────────────────────────────────────────────────────────────────────

function ConfirmScoresPanel({ matchId, compId, match }: {
  matchId: string; compId: string;
  match: MatchCardV2Response['match'];
}) {
  const queryClient = useQueryClient();
  const confirmMutation = useMutation({
    mutationFn: () => api.matchCardConfirmScores(matchId, compId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
      // Prefix match invalidates every standings view (comp-level GET
      // /standings, R2 stage scoreboards, R4 division scoreboards) that keys
      // its query on ['standings', ...] — this write is exactly what makes
      // an OCR-confirmed match visible there for the first time.
      queryClient.invalidateQueries({ queryKey: ['standings'] });
    },
  });

  const existingGames = match.games as MatchCardGameResult[] | undefined;
  const existingWinner = match.nightWinner as string | undefined;
  const alreadyConfirmed = Array.isArray(existingGames) && existingGames.length > 0 && !!existingWinner;
  const result = confirmMutation.data;

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
          <ShieldCheck size={16} /> Confirm scores
        </h3>
        <button
          onClick={() => confirmMutation.mutate()}
          disabled={confirmMutation.isPending}
          className="rounded-md bg-emerald-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {confirmMutation.isPending ? 'Confirming…' : alreadyConfirmed || result ? 'Re-confirm scores' : 'Confirm scores'}
        </button>
      </div>
      <p className="font-mono text-xs text-iron-500">
        Derives per-game results from the OCR'd scoreboards above and writes them into standings —
        the SAME shape a manually-entered result uses. Run this once the scoreboard consensus above looks right.
      </p>
      {confirmMutation.isError && (
        <p className="mt-2 font-mono text-xs text-red-400">{parseApiError(confirmMutation.error).message}</p>
      )}
      {result ? (
        <div className="mt-3 rounded-md border border-emerald-700/50 bg-emerald-950/30 p-3">
          <p className="font-display text-xs font-bold uppercase tracking-wider text-emerald-300">
            Winner: {clanLabel(result.nightWinner, match.clan1, match.clan2, match.clan1DisplayName, match.clan2DisplayName)}
          </p>
          <ul className="mt-1 space-y-0.5 font-mono text-xs text-iron-300">
            {result.games.map((g, i) => (
              <li key={i}>{MODE_LABEL(g.mode)}: {g.clan1Score} – {g.clan2Score} ({clanLabel(g.winner, match.clan1, match.clan2, match.clan1DisplayName, match.clan2DisplayName)})</li>
            ))}
          </ul>
        </div>
      ) : alreadyConfirmed && existingGames && existingWinner && (
        <div className="mt-3 rounded-md border border-iron-700 bg-iron-950 p-3">
          <p className="font-mono text-xs text-iron-400">
            Already confirmed — winner <span className="text-iron-200">{clanLabel(existingWinner, match.clan1, match.clan2, match.clan1DisplayName, match.clan2DisplayName)}</span>, {existingGames.length} game(s) on record.
          </p>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ForgedPanel({ matchId, forgedCardUrl, status, compId, canForge, match }: {
  matchId: string; forgedCardUrl?: string; status: MatchCardStatus; compId: string; canForge: boolean;
  match: MatchCardV2Response['match'];
}) {
  const queryClient = useQueryClient();
  const forgeMutation = useMutation({
    mutationFn: () => api.matchCardForge(matchId, compId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
  });
  const publicUrl = forgedCardUrl ?? `/card/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}`;
  const blocked = isForgeBlocked(match);

  return (
    <section className="rounded-xl border-2 border-forge-500/40 bg-iron-900 p-5">
      <div className="mb-3 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-forge-300">
        <Trophy size={16} /> {status === 'FORGED' ? 'Forged result' : 'Verified — ready to forge'}
      </div>
      {status === 'FORGED' ? (
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={publicUrl}
            className="rounded-md bg-forge-600 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white shadow-[0_0_30px_-8px_rgba(var(--accent-rgb),0.6)] hover:bg-forge-500"
          >
            Open public card
          </Link>
          <button
            onClick={() => navigator.clipboard.writeText(window.location.origin + publicUrl).catch(() => null)}
            className="rounded-md border border-iron-600 bg-iron-800 px-4 py-2 font-mono text-xs text-iron-200 hover:bg-iron-700"
          >
            Copy share link
          </button>
        </div>
      ) : canForge ? (
        <div>
          <button
            onClick={() => forgeMutation.mutate()}
            disabled={forgeMutation.isPending || blocked}
            title={blocked ? FORGE_BLOCKED_HINT : undefined}
            className="rounded-md bg-forge-600 px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-forge-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-forge-600"
          >
            Forge public card
          </button>
          {blocked && (
            <p className="mt-2 font-mono text-xs text-iron-400">{FORGE_BLOCKED_HINT}</p>
          )}
          {forgeMutation.isError && (
            <p className="mt-2 font-mono text-xs text-red-400">{parseApiError(forgeMutation.error).message}</p>
          )}
        </div>
      ) : (
        <p className="font-mono text-xs text-iron-400">Waiting for an admin to forge this card.</p>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function AdminTransitionBar({ matchId, compId, status, match, participant = false }: {
  matchId: string; compId: string; status: MatchCardStatus; match: MatchCardV2Response['match'];
  /** Narrows the bar to the participant-reporting transitions and relabels it
   *  — for a side's leader or its team captain, who are not operators. */
  participant?: boolean;
}) {
  const queryClient = useQueryClient();
  const transitionMutation = useMutation({
    mutationFn: (to: MatchCardStatus) => api.matchCardTransition(matchId, compId, to),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
  });

  // Mirrors ALLOWED_TRANSITIONS in portalApi/matchCardV2.ts — with TWO
  // deliberate narrowings:
  //  - DRAFT→SCHEDULED is dropped from this bar (server still allows it via
  //    a bare POST /transition). That bare flip sets cardStatus only — no
  //    format, no rosters, no planned slots — so a match "transitioned" this
  //    way renders as an empty card forever (format falls back to LEGACY,
  //    gameModes is []). G3b-W0's MatchRosterInitPanel above is now the real
  //    DRAFT→SCHEDULED path (POST .../init, which plans slots FROM real
  //    roster picks); this bar staying silent on that jump for DRAFT stops
  //    it acting as a broken shortcut around it. CANCELLED still needs no
  //    rosters, so it stays.
  //  - VERIFIED→FORGED is dropped here WHEN the result isn't confirmed yet
  //    (isForgeBlocked(match)) — 2026-07-14, the "beautiful final card stuck
  //    on PENDING" trap: this generic bar has no room for the confirm-first
  //    hint ForgedPanel shows, and the server (matchCardV2.ts's
  //    isResultConfirmed guard) rejects the flip anyway — better to not
  //    offer the dead-end click at all and point admins at ForgedPanel's
  //    "Forge public card" button instead, which explains why it's disabled.
  //    Once confirmed, FORGED reappears here too (harmless — same guarded
  //    endpoint either way).
  const allowed: Partial<Record<MatchCardStatus, MatchCardStatus[]>> = {
    DRAFT: ['CANCELLED'],
    SCHEDULED: ['VETO', 'LIVE', 'CANCELLED'],
    VETO: ['LIVE', 'CANCELLED'],
    LIVE: ['CLOSED', 'CANCELLED'],
    CLOSED: ['OCR', 'DISPUTED'],
    OCR: ['VERIFIED', 'DISPUTED'],
    VERIFIED: isForgeBlocked(match) ? ['DISPUTED'] : ['FORGED', 'DISPUTED'],
    DISPUTED: ['VERIFIED', 'CANCELLED'],
    // W-A defect 5: FAILED is never sticky — retry digestion or abandon.
    FAILED: ['OCR', 'CANCELLED'],
  };
  // Participant mode (Lisa, 2026-07-22): a side's leader or the captain of the
  // team it fielded gets the SAME bar, narrowed to the transitions they may
  // actually make — mirrors shared/matchTransitionAuthz.ts's LEADER_ALLOWED_
  // TARGETS exactly. Offering them the operator targets would just be buttons
  // that 403. Note CLOSED is "we've finished playing", NOT a statement that the
  // result is correct — that is the sign-off panel's job.
  const PARTICIPANT_TARGETS = new Set<string>(['SCHEDULED', 'VETO', 'LIVE', 'CLOSED', 'DISPUTED']);
  const nextRaw = allowed[status] ?? [];
  const next = participant ? nextRaw.filter(t => PARTICIPANT_TARGETS.has(t)) : nextRaw;
  if (next.length === 0) return null;

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-950 p-4">
      <div className="mb-2 font-mono text-xs uppercase tracking-wider text-iron-400">
        {participant ? 'Your team · Match status' : 'Admin · Transition'}
      </div>
      <div className="flex flex-wrap gap-2">
        {next.map(to => (
          <button
            key={to}
            onClick={() => transitionMutation.mutate(to)}
            disabled={transitionMutation.isPending}
            className="rounded-md border border-iron-600 bg-iron-800 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wider text-iron-200 hover:bg-iron-700 disabled:opacity-50"
          >
            → {to}
          </button>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CommentsThread({ matchId, compId, comments, me, isAdmin }: {
  matchId: string; compId: string;
  comments: MatchCardV2Response['comments']; me?: Me; isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const postMutation = useMutation({
    mutationFn: (body: string) => api.matchCardComment(matchId, compId, body),
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] });
    },
  });
  const pinMutation = useMutation({
    mutationFn: ({ commentSk, pinned }: { commentSk: string; pinned: boolean }) =>
      api.matchCardPinComment(matchId, commentSk, compId, pinned),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
  });

  const ordered = useMemo(() => {
    const pinned = comments.filter(c => c.pinned);
    const rest = comments.filter(c => !c.pinned);
    return [...pinned, ...rest];
  }, [comments]);

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
        <MessageSquare size={16} /> Chatter
        <span className="font-mono text-[11px] text-iron-500">{comments.length}</span>
      </h3>
      <div className="space-y-2">
        {ordered.map(c => (
          <div
            key={c.sk}
            className={cn(
              'rounded-md px-3 py-2',
              c.pinned ? 'border border-forge-600/40 bg-forge-900/20' : 'bg-iron-950',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-iron-400">
                <span className="font-bold text-iron-200">{c.actorDisplayName ?? c.actorDiscordId.slice(-4)}</span>
                <span className="ml-2 text-iron-500">{new Date(c.ts).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
              </span>
              {isAdmin && (
                <button
                  onClick={() => pinMutation.mutate({ commentSk: c.sk, pinned: !c.pinned })}
                  className="rounded p-1 text-iron-500 hover:bg-iron-700 hover:text-forge-300"
                  title={c.pinned ? 'Unpin' : 'Pin'}
                >
                  <Pin size={12} className={c.pinned ? 'fill-forge-300 text-forge-300' : ''} />
                </button>
              )}
            </div>
            <div className="mt-1 whitespace-pre-wrap break-words text-sm text-iron-100">{c.body}</div>
          </div>
        ))}
        {ordered.length === 0 && <p className="font-mono text-xs text-iron-500">No comments yet.</p>}
      </div>
      {me && (
        <form
          className="mt-3 flex gap-2"
          onSubmit={e => { e.preventDefault(); if (draft.trim()) postMutation.mutate(draft.trim()); }}
        >
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Type a comment…"
            maxLength={2000}
            className="flex-1 rounded-md border border-iron-700 bg-iron-900 px-3 py-2 font-mono text-sm text-iron-100 placeholder:text-iron-500 focus:border-forge-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={postMutation.isPending || !draft.trim()}
            className="rounded-md bg-forge-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-forge-500 disabled:opacity-50"
          >
            Post
          </button>
        </form>
      )}
    </section>
  );
}

export default MatchCardLive;
